import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ja, ko, ru, tr, zh } from './i18n.extra';

// Misma arquitectura que apps/web/src/i18n.tsx (I18nProvider + useI18n + t(key)).
// 5 idiomas originales aquí + 5 de la oleada 2026-07-25 (RU/KO/ZH-TW/JA/TR,
// estudio de mercado: Corea ~36% del revenue del juego, Rusia con demanda
// probada, Taiwán/Japón top-5, Turquía comunidad grande) en i18n.extra.ts.
// La guía (/guia) sigue en los 5 originales: los nuevos la ven en inglés.

export type AppLanguage = 'es' | 'en' | 'fr' | 'de' | 'pt' | 'ru' | 'ko' | 'zh' | 'ja' | 'tr';

const STORAGE_KEY = 'white-bot-dl:language';

export const languages: { code: AppLanguage; label: string }[] = [
  { code: 'es', label: 'Espanol' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Francais' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Portugues' },
  { code: 'ru', label: 'Русский' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '繁體中文' },
  { code: 'ja', label: '日本語' },
  { code: 'tr', label: 'Türkçe' }
];

const es = {
  'app.account': 'Cuenta',
  'app.configuration': 'Configuracion',
  'app.configurationUnavailable': 'Configuracion no disponible',
  'app.faq': 'FAQS',
  'app.faqButton': 'Preguntas frecuentes',
  'app.guide': 'Guía',
  'app.guideButton': 'Ver guía de instalación',
  'app.language': 'Idioma',
  'app.license': 'Licencia',
  'app.manageBilling': 'Gestionar suscripcion',
  'app.subscribe': 'Suscribirse',
  'app.openAccountMenu': 'Abrir menu de cuenta',
  'app.signOut': 'Salir',
  'app.billingError': 'No se pudo abrir el portal de facturacion.',
  'app.billingNoStripe': 'Tu licencia no se gestiona por Stripe (acceso concedido manualmente): no hay pagos que administrar.',
  'app.product': 'White Bot Descargable',

  'auth.title': 'White Bot',
  'auth.subtitle': 'Plan descargable · 11,99 €/mes · prueba de 5 dias',
  'auth.betaSubtitle': 'Beta gratuita',
  'auth.intro': 'Inicia sesion para gestionar tu bot.',
  'auth.email': 'Email',
  'auth.placeholderEmail': 'tu@email.com',
  'auth.sendLink': 'Enviar enlace de acceso',
  'auth.googleLogin': 'Entrar con Google',
  'auth.checkEmail': 'Revisa tu email: te enviamos un enlace de acceso.',
  'auth.loadingSession': 'Cargando sesion…',

  'entitlement.checking': 'Comprobando suscripcion…',
  'entitlement.active': 'activa',
  'entitlement.trialing': 'prueba',
  'entitlement.past_due': 'pago pendiente',
  'entitlement.canceled': 'cancelada',
  'entitlement.inactive': 'inactiva',

  'pills.connected': 'cliente conectado',
  'pills.disconnected': 'cliente desconectado',

  'dashboard.actions': 'Acciones',
  'dashboard.autoBot': 'Auto Bot',
  'dashboard.collect': 'Recolectar',
  'dashboard.trainTroops': 'Train troops',
  'dashboard.autoHunt': 'Auto Hunt',
  'dashboard.stopBot': 'Parar bot',
  'dashboard.screenshot': 'Captura',
  'dashboard.mockView': 'Vista simulada — el cliente aun no ha enviado capturas',
  'dashboard.marches': 'Marchas',
  'dashboard.training': 'Entrenamiento',
  'dashboard.noRecentVerification': 'Sin verificacion reciente',
  'dashboard.queue': 'Cola',
  'dashboard.state': 'Estado',
  'dashboard.activity': 'Actividad',
  'dashboard.lastReport': 'Ultimo reporte',
  'dashboard.clientVersion': 'Version del cliente',
  'dashboard.never': 'nunca',
  'dashboard.disconnectedNotice': 'El cliente no reporta desde hace un rato. Comprueba que esta abierto en tu PC con BlueStacks.',
  'dashboard.settingsSaved': 'Ajustes guardados.',
  'dashboard.client': 'Cliente',
  'dashboard.clientBody': 'Descarga e instala el cliente en tu PC (Windows + BlueStacks). Se activa con tu cuenta.',
  'dashboard.clientDownload': 'Descargar cliente (disponible en GA)',
  'dashboard.clientDownloadReady': 'Descargar cliente (beta)',
  'dashboard.clientSmartscreen': 'Se descarga un ZIP: descomprimelo y ejecuta el instalador. Beta sin firmar: si el navegador o Windows avisan, elige "Conservar" y "Mas informacion → Ejecutar de todas formas".',

  'op.workingEyebrow': 'Operacion en curso',
  'op.successEyebrow': 'Completado',
  'op.errorEyebrow': 'Sin confirmacion',
  'op.warningEyebrow': 'Atencion',
  'op.startTitle': 'Arrancando {action}',
  'op.startBody': 'Orden enviada. El cliente en tu PC la recogera en unos segundos.',
  'op.stopTitle': 'Parando el bot',
  'op.stopBody': 'Orden enviada. El bot terminara el paso en curso y se detendra.',
  'op.successStartTitle': 'Bot en marcha',
  'op.successStartBody': 'El cliente ha confirmado que esta ejecutando.',
  'op.successStopTitle': 'Bot detenido',
  'op.successStopBody': 'El cliente ha confirmado la parada.',
  'op.timeoutTitle': 'El cliente no ha confirmado',
  'op.timeoutBody': 'La orden queda guardada y se aplicara cuando el cliente conecte. Comprueba que el cliente esta abierto en tu PC.',
  'op.disconnectedTitle': 'Cliente desconectado',
  'op.disconnectedBody': 'Tu bot no esta reportando ahora mismo. Abre el cliente en tu PC (con BlueStacks) y vuelve a intentarlo. La orden se puede enviar igualmente.',
  'op.sendAnyway': 'Enviar igualmente',
  'op.close': 'Cerrar',
  'op.cancel': 'Cancelar',

  'settings.level': 'Nivel',
  'settings.marches': 'Marchas',
  'settings.amount': 'Cantidad por tanda',
  'settings.type': 'Tipo',
  'settings.beasts': 'Bestias (10 vigor)',
  'settings.terror': 'Terror polar (25 vigor)',
  'settings.timer': 'Espera de rally',
  'settings.up': 'Subir',
  'settings.down': 'Bajar',
  'settings.resources.iron': 'Hierro',
  'settings.resources.coal': 'Carbon',
  'settings.resources.wood': 'Madera',
  'settings.resources.meat': 'Carne',
  'settings.units.infantry': 'Infanteria',
  'settings.units.lancer': 'Lanceros',
  'settings.units.marksman': 'Tiradores',
  'settings.autoHelp': 'Auto Ayuda',
  'settings.autoHeal': 'Auto Curacion',
  'settings.autoShield': 'Auto Escudo',
  'settings.tasks': 'Tareas',
  'settings.tasksEnable': 'Activar tareas automáticas',
  'settings.tasksHint': 'Recompensas y misiones diarias: correo, héroes gratis, cofre inactivo y misiones de inteligencia. Se hacen solas cada cierto tiempo.',
  'settings.taskMail': 'Correo (reclamar)',
  'settings.taskHeroes': 'Héroes gratis',
  'settings.taskIdle': 'Cofre inactivo',
  'settings.taskIntel': 'Misiones de inteligencia',
  // Mapeo de Intelligence (0.6.4, doc 17)
  'settings.intelMapping': 'Mapeo de inteligencia',
  'settings.intelEnable': 'Activar el mapeo de inteligencia',
  'settings.intelHint': 'Tus bots exploran el mapa de la región elegida y aportan datos a la red de Intelligence. Cada celda escaneada te da tokens para pagar búsquedas.',
  'settings.intelRegion': 'Región a mapear',
  'settings.intelRegionHint': 'De 3 a 5 dígitos (ej. 4347). Normalmente, tu propia región.',
  'settings.intelCheckRegion': 'Comprobar región',
  'settings.intelChecking': 'Comprobando región…',
  'settings.intelRegionReady': 'La región {region} ya está en la red ({total} celdas). Lista para mapear.',
  'settings.intelRegionSeeded': 'Región {region} añadida a la red ({total} celdas). Tus bots ya pueden mapearla.',
  'settings.intelRegionInvalid': 'Región no válida: usa entre 3 y 5 dígitos.',
  'settings.intelRegionError': 'No se pudo comprobar la región. Inténtalo de nuevo.',
  'settings.intelCellsPerPass': 'Celdas por pasada',
  'settings.intelCellsPerPassHint': 'Cuántas celdas escanea el bot en cada turno de mapeo antes de seguir con el resto de tareas.',
  'settings.game': 'Juego',
  'settings.gameLanguage': 'Idioma del juego',
  'settings.gameLanguageEn': 'Ingles (por defecto)',
  'settings.gameLanguageEs': 'Espanol',
  'settings.gameLanguageHint': 'Es el idioma del JUEGO dentro de BlueStacks (no el de esta web). El bot solo sabe leer el juego en inglés o español: ponlo así en los Ajustes del juego → Idioma y elige aquí el mismo.',
  'settings.cryptids': 'Críptidos primero (eventos)',
  'settings.cryptidsHint': 'Si el evento Berserk Cryptid está activo y tienes un Cuerno de Críptido, la caza lo gasta en un críptido; si no hay evento o cuernos, caza lo de siempre (matar bestias es lo que suelta cuernos).',
  'status.hunting_cryptid': 'Buscando críptido del evento…',

  'subscribe.title': 'Prueba White Bot gratis 5 dias',
  'subscribe.body': 'Ejecuta White Bot en tu propio PC (Windows + BlueStacks) y controlalo desde esta web.',
  'subscribe.trial': 'PRUEBA',
  'subscribe.trialValue': '5 dias gratis',
  'subscribe.after': 'DESPUES',
  'subscribe.afterValue': '11,99 € / mes',
  'subscribe.includes': 'INCLUYE',
  'subscribe.includesValue': 'Recoleccion · Tropas · Caza · Curacion · Escudo · Ayuda de alianza · Tareas diarias',
  'subscribe.cancel': 'CANCELA',
  'subscribe.cancelValue': 'Cuando quieras',
  'subscribe.cta': 'Empezar prueba gratis (5 dias)',
  'subscribe.ctaOpening': 'Abriendo pago…',
  'subscribe.cardNote': 'Sin tarjeta para la prueba. Solo pagarás si decides suscribirte cuando acabe.',
  'subscribe.trialCta': 'Empezar prueba gratis de 5 dias — sin tarjeta',
  'subscribe.trialStarting': 'Activando tu prueba…',
  'subscribe.trialError': 'No se pudo activar la prueba. Inténtalo de nuevo.',
  'subscribe.trialUsed': 'La prueba gratis ya se usó en esta cuenta.',
  'subscribe.payCta': 'Suscribirse — 11,99 €/mes',
  'subscribe.endedTitle': 'Tu acceso ha terminado',
  'subscribe.endedBody': 'Tu prueba o suscripción ha finalizado. Suscríbete para seguir usando White Bot en tu PC — tu configuración se conserva.',
  'subscribe.checkoutError': 'Error al iniciar el pago',
  'subscribe.hostedNote': '¿Usas el servicio hosted (corre en nuestros servidores)? Es otro producto:',
  'subscribe.hostedLink': 'ir a whitebot.app',

  // Beta gratuita hasta el 15 de agosto de 2026 (domain/beta.ts)
  'beta.banner': 'Beta gratuita',
  'subscribe.betaTitle': 'White Bot, gratis durante la beta',
  'subscribe.betaTrial': 'BETA',
  'subscribe.betaTrialValue': 'Gratis hasta el 15 ago 2026',
  'subscribe.betaTrialCta': 'Empezar la beta gratis',
  'subscribe.betaCardNote': 'Sin tarjeta. White Bot es gratis durante la beta, hasta el 15 de agosto de 2026.',
  'subscribe.betaEndedNote': 'Estamos en beta gratuita. Si no tienes acceso, escríbenos por el chat de soporte.',

  'tutorial.title': 'Primeros pasos',
  'tutorial.intro': 'Tres pasos para tener tu bot corriendo en tu PC y controlado desde aqui.',
  'tutorial.step1Title': '1. Instala el cliente',
  'tutorial.step1Body': 'Descarga el cliente e instalalo en tu PC junto con BlueStacks. La guia te acompana paso a paso.',
  'tutorial.step2Title': '2. Activalo con tu email',
  'tutorial.step2Body': 'Abre el cliente e introduce tu email. Cuando conecte, aqui veras "cliente conectado".',
  'tutorial.step3Title': '3. Controla el bot desde aqui',
  'tutorial.step3Body': 'Elige modulos y ajustes, pulsa una accion y sigue la captura y el estado en este panel.',
  'tutorial.dontShowAgain': 'No volver a mostrar',
  'tutorial.notNow': 'Ahora no',
  'tutorial.gotIt': 'Entendido',

  'pills.license': 'licencia: {label}',

  'dashboard.refreshCapture': 'Actualizar captura',
  'dashboard.refreshCapturePending': 'Capturando…',
  'dashboard.refreshCaptureTimeout': 'El cliente no ha respondido a la captura. Comprueba que está abierto en tu PC.',

  'state.idle': 'inactivo',
  'state.running': 'en marcha',
  'state.waiting': 'en espera',
  'state.starting': 'arrancando',
  'state.verifying': 'verificando',
  'state.training': 'entrenando',
  'state.collecting': 'recolectando',
  'state.hunting': 'cazando',
  'state.helping': 'ayudando',
  'state.healing': 'curando',
  'state.recovering': 'recuperando',
  'state.reconnecting': 'reconectando',
  'state.shielded': 'escudo activado',
  'state.error': 'error',

  'status.no_client': 'Sin cliente conectado.',
  'status.idle': 'En espera (parado desde la web).',
  'status.running': 'Ejecutando: {modules}',
  'status.running_none': 'Ejecutando (sin módulos activos).',
  'status.shielded': 'Auto Escudo activó la protección: bot parado. Pulsa Parar y de nuevo Iniciar para reanudar.',
  'shieldAlarm.eyebrow': 'Auto Escudo',
  'shieldAlarm.title': 'Te protegimos de un ataque',
  'shieldAlarm.body': 'Detectamos un ataque contra {name} y activamos el escudo automáticamente. Tus tropas se replegaron y tu colonia quedó protegida. El bot se detuvo por seguridad: pulsa Iniciar cuando quieras reanudar.',
  'shieldAlarm.time': 'Hora del ataque',
  'shieldAlarm.close': 'Entendido',
  'status.starting': 'Comprobación de arranque: limpiando pantalla y verificando colonia…',
  'status.waiting': 'Sin acciones pendientes. Próxima comprobación en {time}.',
  'status.backoff_wait': 'Recuperación en curso; reintento en {time}.',
  'status.verifying_training': 'Comprobando colas de entrenamiento…',
  'status.verifying_marches': 'Comprobando colas de marcha…',
  'status.verifying_hunt': 'Comprobando marchas de caza…',
  'status.training_collect': 'Recogiendo tropas completadas: {units}',
  'langWarn.eyebrow': 'Idioma del juego',
  'langWarn.title': 'Pon el juego en inglés',
  'langWarn.body': 'White Bot no puede leer tu juego: el idioma no es compatible. En Whiteout Survival abre Ajustes → Language y elige English. El bot continúa solo en cuanto lo cambies.',
  'langWarn.close': 'Entendido',
  'fleet.title': 'Mis bots',
  'fleet.counter': '{n}/{max} bots',
  'fleet.startAll': 'Iniciar todo',
  'fleet.stopAll': 'Parar todo',
  'fleet.delete': 'Eliminar',
  'fleet.deleteConfirmTitle': '¿Eliminar {name}?',
  'fleet.deleteConfirmBody': 'Se libera un hueco del límite de tu cuenta. Si el cliente de ese bot sigue abierto en tu PC, volverá a aparecer al reportar de nuevo.',
  'fleet.deleteConfirm': 'Eliminar bot',
  'fleet.deleteCancel': 'Cancelar',
  'fleet.back': 'Todos los bots',
  'fleet.empty': 'Aún no hay bots. Abre White Bot Client en tu PC (una consola por instancia de BlueStacks) y aparecerán aquí solos.',
  'fleet.defaultName': 'Bot',
  'fleet.lastReport': 'Último reporte',
  'status.unsupported_language': 'Idioma del juego no compatible — ponlo en inglés',
  'status.language_autofix': 'Idioma no compatible: cambiando el juego a inglés automáticamente…',
  'status.dailies': 'Reclamando recompensas diarias…',
  'status.intel_missions': 'Misiones de inteligencia…',
  'status.intel_map_scanning': 'Inteligencia: {cells} celdas escaneadas (+{tokens} tokens)',
  'status.intel_map_region_done': 'Inteligencia: región al día',
  'status.intel_map_detector_missing': 'Inteligencia: detector no disponible',
  'status.intel_map_region_missing': 'Inteligencia: configura la región en Ajustes',
  'state.intel': 'Inteligencia',
  'state.dailies': 'Recompensas diarias',
  'status.language_fixed': 'Idioma cambiado a inglés; esperando el reinicio del juego…',
  'state.fixing_language': 'Cambiando idioma',
  'state.blocked_language': 'Idioma no compatible',
  'status.training_units': 'Entrenando unidades: {units} ({percent}%)',
  'status.collecting_send': 'Enviando marcha de recolección: {resource} (nivel {level})',
  'status.hunting_send': 'Enviando ataque de caza: {type} nivel {level}',
  'status.helping': 'Comprobando Auto Ayuda de alianza…',
  'status.healing': 'Comprobando Auto Curación…',
  'status.recovering_attempt': 'Recuperando ({attempt}/{max}); reintento en {time}…',
  'status.recovered_ui': 'Pantalla restablecida tras un fallo ({task}). Continuando…',
  'status.relaunching': 'Relanzando BlueStacks/juego tras fallos repetidos…',
  'status.relaunched': 'Juego relanzado tras un fallo. Reanudando en {time}.',
  'status.hand_guide': 'Mano-guía del tutorial detectada: pulsando donde señala…',
  'status.reconnecting': 'Sesión iniciada en otro dispositivo: pulsando Reconectar…',
  'status.backoff_init_error': 'Error de inicialización ({detail}). Reintento automático en {time}.',
  'status.backoff_no_adb': 'Sin conexión con BlueStacks (ADB). Comprueba que BlueStacks está abierto. Reintento en {time}.',
  'status.backoff_failure': 'Fallo repetido ({task}). Reintento automático en {time}.',
  'status.backoff_blocked': 'Pantalla bloqueada ({kind}). Reintento automático en {time}.',

  'statusTask.verify_training': 'verificación de colas de entrenamiento',
  'statusTask.collect_completed': 'recogida de tropas completadas',
  'statusTask.train_flow': 'flujo de entrenamiento',
  'statusTask.return_after_training': 'vuelta a colonia tras entrenar',
  'statusTask.verify_marches': 'verificación de marchas',
  'statusTask.collect_flow': 'flujo de recolección',
  'statusTask.hunt_capture': 'captura del estado de caza',
  'statusTask.verify_hunt': 'verificación de marchas de caza',
  'statusTask.hunt_vigor': 'lectura de vigor de caza',
  'statusTask.hunt_flow': 'flujo de caza',
  'statusTask.auto_heal': 'auto curación',
  'statusTask.startup_return': 'vuelta a colonia en el arranque',
  'statusTask.startup_screen': 'pantalla no reconocida en el arranque',
  'statusTask.unexpected': 'error inesperado',

  'intelligence.title': 'Intelligence',
  'intelligence.intro': 'Consulta datos de jugadores y alianzas recopilados por la red de White Bot. {limit} búsquedas al día.',
  'intelligence.region': 'Región',
  'intelligence.regionPlaceholder': 'Región (ej. 4347)',
  'intelligence.searchPlayer': 'Buscar jugador o alianza',
  'intelligence.searchPlaceholder': 'Jugador o alianza (vacío = toda la región)',
  'intelligence.regionSearchTitle': 'Toda la región {region}',
  'intelligence.errorUnknownRegion': 'Esa región aún no está en la red (revisa el número).',
  'intelligence.quotaRemainingValue': '{remaining} de {limit}',
  'intelligence.request': 'Solicitar',
  'intelligence.requesting': 'Solicitando…',
  'intelligence.quotaRemaining': 'Búsquedas disponibles hoy',
  'intelligence.minChars': 'Escribe al menos 3 caracteres, o deja el campo vacío para ver toda la región.',
  'intelligence.regionRequired': 'Indica la región donde buscar (ej. 4347).',
  'intelligence.loading': 'Cargando búsquedas…',
  'intelligence.empty': 'No hay búsquedas todavía.',
  'intelligence.delete': 'Eliminar búsqueda',
  'intelligence.power': 'Poder',
  'intelligence.coordinates': 'Coordenadas',
  'intelligence.notFound': 'Sin coincidencias.',
  'intelligence.noResult': 'Resultado pendiente (aún sin datos en esa región).',
  'intelligence.statusPending': 'En cola',
  'intelligence.statusProcessing': 'Buscando',
  'intelligence.statusCompleted': 'Completada',
  'intelligence.statusNotFound': 'No encontrado',
  'intelligence.statusFailed': 'No se pudo obtener el resultado',
  'intelligence.errorQuota': 'Has agotado tus búsquedas de hoy. Vuelve mañana.',
  'intelligence.errorPending': 'Ya tienes esa búsqueda en curso.',
  'intelligence.errorNoRegion': 'Indica una región válida donde buscar.',
  'intelligence.errorGeneric': 'No se pudo realizar la búsqueda. Inténtalo de nuevo.',
  'intelligence.search': 'Búsqueda',
  'intelligence.resultsFound': '{count} resultados',
  'intelligence.showMore': 'Ver {count} más',
  'intelligence.showLess': 'Ver menos',
  'intelligence.sortResults': 'Ordenar resultados',
  'intelligence.sortPower': 'Poder',
  'intelligence.sortName': 'Nombre',
  'intelligence.lastSeen': 'Visto',
  'intelligence.deleteConfirm': '¿Eliminar "{name}" del panel? La búsqueda seguirá contando para tu límite diario.',
  // Economía de tokens + progreso del mapeo (0.6.4, doc 17)
  'intelligence.tokensChip': '{n} tokens',
  'intelligence.freeChip': '{n} gratis hoy',
  'intelligence.freeTodayLabel': 'Búsqueda gratis de hoy',
  'intelligence.tokensBalance': 'Saldo de tokens',
  'intelligence.costsLine': 'Coste en tokens: búsqueda {search} · toda la región {region}',
  'intelligence.noTokensCta': 'Sin búsqueda gratis ni tokens: activa el mapeo de inteligencia en Ajustes y tus bots ganarán tokens escaneando el mapa.',
  'intelligence.errorTokens': 'No tienes tokens suficientes para esta búsqueda. Activa el mapeo para ganar más.',
  'intelligence.mappingTitle': 'Progreso del mapeo',
  'intelligence.mappingCells': '{scanned} de {total} celdas',
  'intelligence.mappingToday': 'Escaneadas hoy por tus bots',
  'intelligence.mappingEarnedToday': 'Tokens ganados hoy',
  'shieldWatch.toggle': 'Vigilar el escudo de un jugador (este bot deja de mapear)',
  'shieldWatch.title': 'Shield Monitor',
  'shieldWatch.historyTitle': 'Historial de la vigilancia',
  'activity.title': 'Actividad de Inteligencia',
  'activity.showAll': 'Ver todo ({count})',
  'activity.showLess': 'Mostrar menos',
  'activity.protected': 'Ataque detectado: te protegimos con escudo',
  'activity.attackHalted': 'Ataque detectado: escudo NO confirmado',
  'shieldWatch.captureOpen': 'Ver la captura de ese momento',
  'shieldWatch.captureLoading': 'Cargando la captura…',
  'shieldWatch.captureAlt': 'Captura del momento en que el bot decidió',
  'shieldWatch.captureGone': 'Esta captura ya no está: solo se guardan las 5 últimas de cada bot.',
  'shieldWatch.captureClose': 'Cerrar',
  'shieldWatch.help': 'Escribe las coordenadas del castillo a vigilar. El bot comprobará cada cierto tiempo si sigue teniendo escudo y te avisará por correo cuando se le caiga.',
  'shieldWatch.interval': 'Cada',
  'shieldWatch.region': 'Región',
  'shieldWatch.needCoords': 'Indica las coordenadas X e Y del jugador a vigilar.',
  'shieldWatch.idleNote': 'Las comprobaciones se hacen en los ratos libres del bot; con la granja muy ocupada pueden espaciarse.',
  'shieldWatch.stateShielded': 'El objetivo SIGUE con escudo',
  'shieldWatch.stateConfirming': 'Sin escudo: confirmando…',
  'shieldWatch.stateDown': '¡Escudo CAÍDO!',
  'shieldWatch.stateMoved': 'El objetivo ya no está en esas coordenadas',
  'shieldWatch.stateNoDetector': 'No se puede vigilar: detector no disponible',
  'intelligence.mappingNoRegion': 'Elige y comprueba una región en Ajustes → Mapeo de inteligencia para ver su progreso.',

  'support.title': 'Soporte',
  'support.subtitle': 'Atención al cliente — te respondemos aquí mismo.',
  'support.open': 'Abrir el chat de soporte',
  'support.close': 'Cerrar el chat de soporte',
  'support.empty': '¿Necesitas ayuda? Cuéntanos el problema (puedes adjuntar una captura) y te responderemos en este chat.',
  'support.placeholder': 'Escribe tu mensaje…',
  'support.send': 'Enviar',
  'support.sending': 'Enviando…',
  'support.attach': 'Adjuntar captura',
  'support.attachRemove': 'Quitar la imagen',
  'support.attachTooBig': 'La imagen supera los 5 MB.',
  'support.attachNotImage': 'Solo se pueden adjuntar imágenes.',
  'support.imageAlt': 'Captura adjunta',
  'support.you': 'Tú',
  'support.agent': 'Soporte',
  'support.sendError': 'No se pudo enviar el mensaje. Inténtalo de nuevo.',
  'support.loading': 'Cargando conversación…',
  'support.unreadLabel': '{count} mensajes nuevos de soporte'
};

const en: Record<TranslationKey, string> = {
  'app.account': 'Account',
  'app.configuration': 'Configuration',
  'app.configurationUnavailable': 'Configuration not available',
  'app.faq': 'FAQS',
  'app.faqButton': 'Frequently asked questions',
  'app.guide': 'Guide',
  'app.guideButton': 'View installation guide',
  'app.language': 'Language',
  'app.license': 'License',
  'app.manageBilling': 'Manage subscription',
  'app.subscribe': 'Subscribe',
  'app.openAccountMenu': 'Open account menu',
  'app.signOut': 'Sign out',
  'app.billingError': 'Could not open the billing portal.',
  'app.billingNoStripe': 'Your license is not managed through Stripe (access granted manually): there are no payments to manage.',
  'app.product': 'White Bot Desktop',

  'auth.title': 'White Bot',
  'auth.subtitle': 'Desktop plan · €11.99/month · 5-day trial',
  'auth.betaSubtitle': 'Free beta',
  'auth.intro': 'Sign in to manage your bot.',
  'auth.email': 'Email',
  'auth.placeholderEmail': 'you@email.com',
  'auth.sendLink': 'Send access link',
  'auth.googleLogin': 'Sign in with Google',
  'auth.checkEmail': 'Check your email: we sent you an access link.',
  'auth.loadingSession': 'Loading session…',

  'entitlement.checking': 'Checking subscription…',
  'entitlement.active': 'active',
  'entitlement.trialing': 'trial',
  'entitlement.past_due': 'payment due',
  'entitlement.canceled': 'canceled',
  'entitlement.inactive': 'inactive',

  'pills.connected': 'client connected',
  'pills.disconnected': 'client disconnected',

  'dashboard.actions': 'Actions',
  'dashboard.autoBot': 'Auto Bot',
  'dashboard.collect': 'Collect',
  'dashboard.trainTroops': 'Train troops',
  'dashboard.autoHunt': 'Auto Hunt',
  'dashboard.stopBot': 'Stop bot',
  'dashboard.screenshot': 'Screenshot',
  'dashboard.mockView': 'Simulated view — the client has not sent captures yet',
  'dashboard.marches': 'Marches',
  'dashboard.training': 'Training',
  'dashboard.noRecentVerification': 'No recent verification',
  'dashboard.queue': 'Queue',
  'dashboard.state': 'State',
  'dashboard.activity': 'Activity',
  'dashboard.lastReport': 'Last report',
  'dashboard.clientVersion': 'Client version',
  'dashboard.never': 'never',
  'dashboard.disconnectedNotice': 'The client has not reported for a while. Check that it is open on your PC with BlueStacks.',
  'dashboard.settingsSaved': 'Settings saved.',
  'dashboard.client': 'Client',
  'dashboard.clientBody': 'Download and install the client on your PC (Windows + BlueStacks). It activates with your account.',
  'dashboard.clientDownload': 'Download client (available at GA)',
  'dashboard.clientDownloadReady': 'Download client (beta)',
  'dashboard.clientSmartscreen': 'Downloads as a ZIP: extract it and run the installer. Unsigned beta: if the browser or Windows warn you, choose "Keep" and "More info → Run anyway".',

  'op.workingEyebrow': 'Operation in progress',
  'op.successEyebrow': 'Completed',
  'op.errorEyebrow': 'No confirmation',
  'op.warningEyebrow': 'Attention',
  'op.startTitle': 'Starting {action}',
  'op.startBody': 'Order sent. The client on your PC will pick it up in a few seconds.',
  'op.stopTitle': 'Stopping the bot',
  'op.stopBody': 'Order sent. The bot will finish the current step and stop.',
  'op.successStartTitle': 'Bot running',
  'op.successStartBody': 'The client confirmed it is executing.',
  'op.successStopTitle': 'Bot stopped',
  'op.successStopBody': 'The client confirmed the stop.',
  'op.timeoutTitle': 'The client did not confirm',
  'op.timeoutBody': 'The order is saved and will apply when the client connects. Check that the client is open on your PC.',
  'op.disconnectedTitle': 'Client disconnected',
  'op.disconnectedBody': 'Your bot is not reporting right now. Open the client on your PC (with BlueStacks) and try again. You can still send the order.',
  'op.sendAnyway': 'Send anyway',
  'op.close': 'Close',
  'op.cancel': 'Cancel',

  'settings.level': 'Level',
  'settings.marches': 'Marches',
  'settings.amount': 'Amount per batch',
  'settings.type': 'Type',
  'settings.beasts': 'Beasts (10 stamina)',
  'settings.terror': 'Polar terror (25 stamina)',
  'settings.timer': 'Rally wait',
  'settings.up': 'Increase',
  'settings.down': 'Decrease',
  'settings.resources.iron': 'Iron',
  'settings.resources.coal': 'Coal',
  'settings.resources.wood': 'Wood',
  'settings.resources.meat': 'Meat',
  'settings.units.infantry': 'Infantry',
  'settings.units.lancer': 'Lancers',
  'settings.units.marksman': 'Marksmen',
  'settings.autoHelp': 'Auto Help',
  'settings.autoHeal': 'Auto Heal',
  'settings.autoShield': 'Auto Shield',
  'settings.tasks': 'Tasks',
  'settings.tasksEnable': 'Enable automatic tasks',
  'settings.tasksHint': 'Daily rewards and missions: mail, free heroes, idle chest and intel missions. Done automatically on a schedule.',
  'settings.taskMail': 'Mail (claim)',
  'settings.taskHeroes': 'Free heroes',
  'settings.taskIdle': 'Idle chest',
  'settings.taskIntel': 'Intel missions',
  // Intelligence mapping (0.6.4, doc 17)
  'settings.intelMapping': 'Intelligence mapping',
  'settings.intelEnable': 'Enable intelligence mapping',
  'settings.intelHint': 'Your bots explore the map of the chosen region and feed the Intelligence network. Every scanned cell earns you tokens to pay for searches.',
  'settings.intelRegion': 'Region to map',
  'settings.intelRegionHint': '3 to 5 digits (e.g. 4347). Usually your own region.',
  'settings.intelCheckRegion': 'Check region',
  'settings.intelChecking': 'Checking region…',
  'settings.intelRegionReady': 'Region {region} is already in the network ({total} cells). Ready to map.',
  'settings.intelRegionSeeded': 'Region {region} added to the network ({total} cells). Your bots can start mapping it.',
  'settings.intelRegionInvalid': 'Invalid region: use 3 to 5 digits.',
  'settings.intelRegionError': 'Could not check the region. Please try again.',
  'settings.intelCellsPerPass': 'Cells per pass',
  'settings.intelCellsPerPassHint': 'How many cells the bot scans on each mapping turn before moving on to its other tasks.',
  'settings.game': 'Game',
  'settings.gameLanguage': 'Game language',
  'settings.gameLanguageEn': 'English (default)',
  'settings.gameLanguageEs': 'Spanish',
  'settings.gameLanguageHint': 'This is the GAME language inside BlueStacks (not this website\'s). The bot can only read the game in English or Spanish: set it in the game\'s Settings → Language and pick the same one here.',
  'settings.cryptids': 'Cryptids first (events)',
  'settings.cryptidsHint': 'When the Berserk Cryptid event is active and you have a Cryptid Horn, the hunt spends it on a cryptid; with no event or horns it hunts as usual (killing beasts is what drops horns).',
  'status.hunting_cryptid': 'Looking for an event cryptid…',

  'subscribe.title': 'Try White Bot free for 5 days',
  'subscribe.body': 'Run White Bot on your own PC (Windows + BlueStacks) and control it from this web.',
  'subscribe.trial': 'TRIAL',
  'subscribe.trialValue': '5 days free',
  'subscribe.after': 'AFTER',
  'subscribe.afterValue': '€11.99 / month',
  'subscribe.includes': 'INCLUDES',
  'subscribe.includesValue': 'Gathering · Troops · Hunting · Healing · Shield · Alliance help · Daily tasks',
  'subscribe.cancel': 'CANCEL',
  'subscribe.cancelValue': 'Anytime',
  'subscribe.cta': 'Start free trial (5 days)',
  'subscribe.ctaOpening': 'Opening payment…',
  'subscribe.cardNote': 'No card needed for the trial. You only pay if you decide to subscribe when it ends.',
  'subscribe.trialCta': 'Start your free 5-day trial — no card',
  'subscribe.trialStarting': 'Activating your trial…',
  'subscribe.trialError': 'Could not activate the trial. Please try again.',
  'subscribe.trialUsed': 'The free trial was already used on this account.',
  'subscribe.payCta': 'Subscribe — €11.99/month',
  'subscribe.endedTitle': 'Your access has ended',
  'subscribe.endedBody': 'Your trial or subscription has ended. Subscribe to keep using White Bot on your PC — your settings are kept.',
  'subscribe.checkoutError': 'Error starting the payment',
  'subscribe.hostedNote': 'Using the hosted service (runs on our servers)? It is a different product:',
  'subscribe.hostedLink': 'go to whitebot.app',

  // Free beta until August 15, 2026 (domain/beta.ts)
  'beta.banner': 'Free beta',
  'subscribe.betaTitle': 'White Bot, free during the beta',
  'subscribe.betaTrial': 'BETA',
  'subscribe.betaTrialValue': 'Free until Aug 15, 2026',
  'subscribe.betaTrialCta': 'Start the free beta',
  'subscribe.betaCardNote': 'No card needed. White Bot is free during the beta, until August 15, 2026.',
  'subscribe.betaEndedNote': 'We are in free beta. If you don\'t have access, message us in the support chat.',

  'tutorial.title': 'First steps',
  'tutorial.intro': 'Three steps to get your bot running on your PC, controlled from here.',
  'tutorial.step1Title': '1. Install the client',
  'tutorial.step1Body': 'Download the client and install it on your PC along with BlueStacks. The guide walks you through it.',
  'tutorial.step2Title': '2. Activate it with your email',
  'tutorial.step2Body': 'Open the client and enter your email. When it connects, you will see "client connected" here.',
  'tutorial.step3Title': '3. Control the bot from here',
  'tutorial.step3Body': 'Pick modules and settings, press an action and follow the capture and state on this panel.',
  'tutorial.dontShowAgain': 'Do not show again',
  'tutorial.notNow': 'Not now',
  'tutorial.gotIt': 'Got it',

  'pills.license': 'license: {label}',

  'dashboard.refreshCapture': 'Refresh capture',
  'dashboard.refreshCapturePending': 'Capturing…',
  'dashboard.refreshCaptureTimeout': 'The client did not answer the capture request. Check that it is open on your PC.',

  'state.idle': 'idle',
  'state.running': 'running',
  'state.waiting': 'waiting',
  'state.starting': 'starting',
  'state.verifying': 'verifying',
  'state.training': 'training',
  'state.collecting': 'gathering',
  'state.hunting': 'hunting',
  'state.helping': 'helping',
  'state.healing': 'healing',
  'state.recovering': 'recovering',
  'state.reconnecting': 'reconnecting',
  'state.shielded': 'shield active',
  'state.error': 'error',

  'status.no_client': 'No client connected.',
  'status.idle': 'Standing by (stopped from the web).',
  'status.running': 'Running: {modules}',
  'status.running_none': 'Running (no active modules).',
  'status.shielded': 'Auto Shield engaged the protection: bot stopped. Press Stop and then Start again to resume.',
  'shieldAlarm.eyebrow': 'Auto Shield',
  'shieldAlarm.title': 'We protected you from an attack',
  'shieldAlarm.body': 'We detected an attack on {name} and raised your shield automatically. Your troops were recalled and your colony is safe. The bot stopped for safety — press Start whenever you want to resume.',
  'shieldAlarm.time': 'Attack time',
  'shieldAlarm.close': 'Got it',
  'status.starting': 'Startup check: clearing the screen and verifying the colony…',
  'status.waiting': 'Nothing to do. Next check in {time}.',
  'status.backoff_wait': 'Recovery in progress; retrying in {time}.',
  'status.verifying_training': 'Checking training queues…',
  'status.verifying_marches': 'Checking march queues…',
  'status.verifying_hunt': 'Checking hunt marches…',
  'status.training_collect': 'Collecting completed troops: {units}',
  'langWarn.eyebrow': 'Game language',
  'langWarn.title': 'Set the game to English',
  'langWarn.body': "White Bot can't read your game: its language isn't supported. In Whiteout Survival open Settings → Language and choose English. The bot resumes on its own once you switch.",
  'langWarn.close': 'Got it',
  'fleet.title': 'My bots',
  'fleet.counter': '{n}/{max} bots',
  'fleet.startAll': 'Start all',
  'fleet.stopAll': 'Stop all',
  'fleet.delete': 'Delete',
  'fleet.deleteConfirmTitle': 'Delete {name}?',
  'fleet.deleteConfirmBody': 'This frees a slot of your account limit. If that bot\'s client is still open on your PC, it will reappear on its next report.',
  'fleet.deleteConfirm': 'Delete bot',
  'fleet.deleteCancel': 'Cancel',
  'fleet.back': 'All bots',
  'fleet.empty': 'No bots yet. Open White Bot Client on your PC (one console per BlueStacks instance) and they will show up here on their own.',
  'fleet.defaultName': 'Bot',
  'fleet.lastReport': 'Last report',
  'status.unsupported_language': 'Game language not supported — switch it to English',
  'status.language_autofix': 'Unsupported game language: switching the game to English automatically…',
  'status.dailies': 'Claiming daily rewards…',
  'status.intel_missions': 'Intel missions…',
  'status.intel_map_scanning': 'Intelligence: {cells} cells scanned (+{tokens} tokens)',
  'status.intel_map_region_done': 'Intelligence: region up to date',
  'status.intel_map_detector_missing': 'Intelligence: detector not available',
  'status.intel_map_region_missing': 'Intelligence: set the region in Settings',
  'state.intel': 'Intel',
  'state.dailies': 'Daily rewards',
  'status.language_fixed': 'Language switched to English; waiting for the game to restart…',
  'state.fixing_language': 'Fixing language',
  'state.blocked_language': 'Language not supported',
  'status.training_units': 'Training units: {units} ({percent}%)',
  'status.collecting_send': 'Sending gathering march: {resource} (level {level})',
  'status.hunting_send': 'Sending hunt attack: {type} level {level}',
  'status.helping': 'Checking alliance Auto Help…',
  'status.healing': 'Checking Auto Heal…',
  'status.recovering_attempt': 'Recovering ({attempt}/{max}); retrying in {time}…',
  'status.recovered_ui': 'Screen restored after a failure ({task}). Continuing…',
  'status.relaunching': 'Relaunching BlueStacks/game after repeated failures…',
  'status.relaunched': 'Game relaunched after a failure. Resuming in {time}.',
  'status.hand_guide': 'Tutorial hand detected: tapping where it points…',
  'status.reconnecting': 'Session opened on another device: pressing Reconnect…',
  'status.backoff_init_error': 'Initialization error ({detail}). Automatic retry in {time}.',
  'status.backoff_no_adb': 'No connection to BlueStacks (ADB). Check that BlueStacks is open. Retrying in {time}.',
  'status.backoff_failure': 'Repeated failure ({task}). Automatic retry in {time}.',
  'status.backoff_blocked': 'Screen blocked ({kind}). Automatic retry in {time}.',

  'statusTask.verify_training': 'training queue check',
  'statusTask.collect_completed': 'completed troop collection',
  'statusTask.train_flow': 'training flow',
  'statusTask.return_after_training': 'return to colony after training',
  'statusTask.verify_marches': 'march check',
  'statusTask.collect_flow': 'gathering flow',
  'statusTask.hunt_capture': 'hunt status capture',
  'statusTask.verify_hunt': 'hunt march check',
  'statusTask.hunt_vigor': 'hunt vigor reading',
  'statusTask.hunt_flow': 'hunt flow',
  'statusTask.auto_heal': 'auto heal',
  'statusTask.startup_return': 'return to colony at startup',
  'statusTask.startup_screen': 'unrecognized screen at startup',
  'statusTask.unexpected': 'unexpected error',

  'intelligence.title': 'Intelligence',
  'intelligence.intro': 'Look up players and alliances gathered by the White Bot network. {limit} searches per day.',
  'intelligence.region': 'Region',
  'intelligence.regionPlaceholder': 'Region (e.g. 4347)',
  'intelligence.searchPlayer': 'Search player or alliance',
  'intelligence.searchPlaceholder': 'Player or alliance (empty = whole region)',
  'intelligence.regionSearchTitle': 'Whole region {region}',
  'intelligence.errorUnknownRegion': 'That region is not in the network yet (check the number).',
  'intelligence.quotaRemainingValue': '{remaining} of {limit}',
  'intelligence.request': 'Search',
  'intelligence.requesting': 'Searching…',
  'intelligence.quotaRemaining': 'Searches left today',
  'intelligence.minChars': 'Type at least 3 characters, or leave it empty to list the whole region.',
  'intelligence.regionRequired': 'Enter the region to search (e.g. 4347).',
  'intelligence.loading': 'Loading searches…',
  'intelligence.empty': 'No searches yet.',
  'intelligence.delete': 'Delete search',
  'intelligence.power': 'Power',
  'intelligence.coordinates': 'Coordinates',
  'intelligence.notFound': 'No matches.',
  'intelligence.noResult': 'Result pending (no data in that region yet).',
  'intelligence.statusPending': 'Queued',
  'intelligence.statusProcessing': 'Searching',
  'intelligence.statusCompleted': 'Completed',
  'intelligence.statusNotFound': 'Not found',
  'intelligence.statusFailed': 'Could not fetch the result',
  'intelligence.errorQuota': 'You have used all of today\'s searches. Come back tomorrow.',
  'intelligence.errorPending': 'That search is already in progress.',
  'intelligence.errorNoRegion': 'Enter a valid region to search.',
  'intelligence.errorGeneric': 'The search could not be completed. Please try again.',
  'intelligence.search': 'Search',
  'intelligence.resultsFound': '{count} results',
  'intelligence.showMore': 'Show {count} more',
  'intelligence.showLess': 'Show less',
  'intelligence.sortResults': 'Sort results',
  'intelligence.sortPower': 'Power',
  'intelligence.sortName': 'Name',
  'intelligence.lastSeen': 'Seen',
  'intelligence.deleteConfirm': 'Remove "{name}" from the panel? The search still counts toward your daily limit.',
  // Token economy + mapping progress (0.6.4, doc 17)
  'intelligence.tokensChip': '{n} tokens',
  'intelligence.freeChip': '{n} free today',
  'intelligence.freeTodayLabel': "Today's free search",
  'intelligence.tokensBalance': 'Token balance',
  'intelligence.costsLine': 'Token cost: search {search} · whole region {region}',
  'intelligence.noTokensCta': 'No free search or tokens left: enable intelligence mapping in Settings and your bots will earn tokens by scanning the map.',
  'intelligence.errorTokens': 'Not enough tokens for this search. Enable mapping to earn more.',
  'intelligence.mappingTitle': 'Mapping progress',
  'intelligence.mappingCells': '{scanned} of {total} cells',
  'intelligence.mappingToday': 'Scanned today by your bots',
  'intelligence.mappingEarnedToday': 'Tokens earned today',
  'shieldWatch.toggle': 'Watch a player\'s shield (this bot stops mapping)',
  'shieldWatch.title': 'Shield Monitor',
  'shieldWatch.historyTitle': 'Watch history',
  'activity.title': 'Intelligence activity',
  'activity.showAll': 'Show all ({count})',
  'activity.showLess': 'Show less',
  'activity.protected': 'Attack detected: we shielded you',
  'activity.attackHalted': 'Attack detected: shield NOT confirmed',
  'shieldWatch.captureOpen': 'View the screenshot of that moment',
  'shieldWatch.captureLoading': 'Loading the screenshot…',
  'shieldWatch.captureAlt': 'Screenshot from the moment the bot decided',
  'shieldWatch.captureGone': 'This screenshot is gone: only the last 5 per bot are kept.',
  'shieldWatch.captureClose': 'Close',
  'shieldWatch.help': 'Enter the coordinates of the castle to watch. The bot will check periodically whether it still has a shield and email you when it drops.',
  'shieldWatch.interval': 'Every',
  'shieldWatch.region': 'Region',
  'shieldWatch.needCoords': 'Enter the X and Y coordinates of the player to watch.',
  'shieldWatch.idleNote': 'Checks run in the bot\'s idle gaps; with a busy farm they may be spaced out.',
  'shieldWatch.stateShielded': 'Target STILL shielded',
  'shieldWatch.stateConfirming': 'No shield: confirming…',
  'shieldWatch.stateDown': 'Shield DOWN!',
  'shieldWatch.stateMoved': 'The target is no longer at those coordinates',
  'shieldWatch.stateNoDetector': 'Cannot watch: detector unavailable',
  'intelligence.mappingNoRegion': 'Pick and check a region in Settings → Intelligence mapping to see its progress.',

  'support.title': 'Support',
  'support.subtitle': 'Customer support — we reply right here.',
  'support.open': 'Open the support chat',
  'support.close': 'Close the support chat',
  'support.empty': 'Need help? Tell us about the problem (you can attach a screenshot) and we will reply in this chat.',
  'support.placeholder': 'Type your message…',
  'support.send': 'Send',
  'support.sending': 'Sending…',
  'support.attach': 'Attach a screenshot',
  'support.attachRemove': 'Remove the image',
  'support.attachTooBig': 'The image is larger than 5 MB.',
  'support.attachNotImage': 'Only images can be attached.',
  'support.imageAlt': 'Attached screenshot',
  'support.you': 'You',
  'support.agent': 'Support',
  'support.sendError': 'The message could not be sent. Please try again.',
  'support.loading': 'Loading conversation…',
  'support.unreadLabel': '{count} new support messages'
};

const fr: Record<TranslationKey, string> = {
  'app.account': 'Compte',
  'app.configuration': 'Configuration',
  'app.configurationUnavailable': 'Configuration non disponible',
  'app.faq': 'FAQ',
  'app.faqButton': 'Questions frequentes',
  'app.language': 'Langue',
  'app.license': 'Licence',
  'app.manageBilling': 'Gerer l\'abonnement',
  'app.subscribe': 'S\'abonner',
  'app.openAccountMenu': 'Ouvrir le menu du compte',
  'app.signOut': 'Deconnexion',
  'app.billingError': 'Impossible d\'ouvrir le portail de facturation.',
  'app.billingNoStripe': 'Ta licence n\'est pas gérée via Stripe (accès accordé manuellement) : aucun paiement à gérer.',
  'app.product': 'White Bot Desktop',
  'app.guide': 'Guide',
  'app.guideButton': 'Voir le guide d\'installation',

  'auth.title': 'White Bot',
  'auth.subtitle': 'Offre Desktop · 11,99 €/mois · 5 jours d\'essai',
  'auth.betaSubtitle': 'Bêta gratuite',
  'auth.intro': 'Connectez-vous pour gerer votre bot.',
  'auth.email': 'Email',
  'auth.placeholderEmail': 'vous@email.com',
  'auth.sendLink': 'Envoyer le lien d\'acces',
  'auth.googleLogin': 'Se connecter avec Google',
  'auth.checkEmail': 'Verifiez votre email : nous vous avons envoye un lien d\'acces.',
  'auth.loadingSession': 'Chargement de la session…',

  'entitlement.checking': 'Verification de l\'abonnement…',
  'entitlement.active': 'active',
  'entitlement.trialing': 'essai',
  'entitlement.past_due': 'paiement en attente',
  'entitlement.canceled': 'annule',
  'entitlement.inactive': 'inactif',

  'pills.connected': 'client connecte',
  'pills.disconnected': 'client deconnecte',

  'dashboard.actions': 'Actions',
  'dashboard.autoBot': 'Auto Bot',
  'dashboard.collect': 'Collecter',
  'dashboard.trainTroops': 'Entrainer troupes',
  'dashboard.autoHunt': 'Auto Hunt',
  'dashboard.stopBot': 'Arreter le bot',
  'dashboard.screenshot': 'Capture',
  'dashboard.mockView': 'Vue simulee — le client n\'a pas encore envoye de captures',
  'dashboard.marches': 'Marches',
  'dashboard.training': 'Entrainement',
  'dashboard.noRecentVerification': 'Aucune verification recente',
  'dashboard.queue': 'File',
  'dashboard.state': 'Etat',
  'dashboard.activity': 'Activite',
  'dashboard.lastReport': 'Dernier rapport',
  'dashboard.clientVersion': 'Version du client',
  'dashboard.never': 'jamais',
  'dashboard.disconnectedNotice': 'Le client ne rapporte plus depuis un moment. Verifiez qu\'il est ouvert sur votre PC avec BlueStacks.',
  'dashboard.settingsSaved': 'Reglages enregistres.',
  'dashboard.client': 'Client',
  'dashboard.clientBody': 'Telechargez et installez le client sur votre PC (Windows + BlueStacks). Il s\'active avec votre compte.',
  'dashboard.clientDownload': 'Telecharger le client (disponible en GA)',
  'dashboard.clientDownloadReady': 'Telecharger le client (beta)',
  'dashboard.clientSmartscreen': 'Telechargement en ZIP : extrayez-le et lancez l\'installateur. Beta non signee : si le navigateur ou Windows vous avertissent, choisissez "Conserver" puis "Informations complementaires → Executer quand meme".',

  'op.workingEyebrow': 'Operation en cours',
  'op.successEyebrow': 'Termine',
  'op.errorEyebrow': 'Sans confirmation',
  'op.warningEyebrow': 'Attention',
  'op.startTitle': 'Demarrage de {action}',
  'op.startBody': 'Ordre envoye. Le client sur votre PC le recuperera dans quelques secondes.',
  'op.stopTitle': 'Arret du bot',
  'op.stopBody': 'Ordre envoye. Le bot terminera l\'etape en cours puis s\'arretera.',
  'op.successStartTitle': 'Bot en marche',
  'op.successStartBody': 'Le client a confirme qu\'il est en cours d\'execution.',
  'op.successStopTitle': 'Bot arrete',
  'op.successStopBody': 'Le client a confirme l\'arret.',
  'op.timeoutTitle': 'Le client n\'a pas confirme',
  'op.timeoutBody': 'L\'ordre est enregistre et s\'appliquera quand le client se connectera. Verifiez que le client est ouvert sur votre PC.',
  'op.disconnectedTitle': 'Client deconnecte',
  'op.disconnectedBody': 'Votre bot ne rapporte pas en ce moment. Ouvrez le client sur votre PC (avec BlueStacks) et reessayez. Vous pouvez quand meme envoyer l\'ordre.',
  'op.sendAnyway': 'Envoyer quand meme',
  'op.close': 'Fermer',
  'op.cancel': 'Annuler',

  'settings.level': 'Niveau',
  'settings.marches': 'Marches',
  'settings.amount': 'Quantite par lot',
  'settings.type': 'Type',
  'settings.beasts': 'Betes (10 vigueur)',
  'settings.terror': 'Terreur polaire (25 vigueur)',
  'settings.timer': 'Attente de rally',
  'settings.up': 'Augmenter',
  'settings.down': 'Diminuer',
  'settings.resources.iron': 'Fer',
  'settings.resources.coal': 'Charbon',
  'settings.resources.wood': 'Bois',
  'settings.resources.meat': 'Viande',
  'settings.units.infantry': 'Infanterie',
  'settings.units.lancer': 'Lanciers',
  'settings.units.marksman': 'Tireurs',
  'settings.autoHelp': 'Auto Aide',
  'settings.autoHeal': 'Auto Soin',
  'settings.autoShield': 'Auto Bouclier',
  'settings.tasks': 'Tâches',
  'settings.tasksEnable': 'Activer les tâches automatiques',
  'settings.tasksHint': 'Récompenses et missions quotidiennes : courrier, héros gratuits, coffre inactif et missions de renseignement. Faites automatiquement.',
  'settings.taskMail': 'Courrier (réclamer)',
  'settings.taskHeroes': 'Héros gratuits',
  'settings.taskIdle': 'Coffre inactif',
  'settings.taskIntel': 'Missions de renseignement',
  // Cartographie de renseignement (0.6.4, doc 17)
  'settings.intelMapping': 'Cartographie de renseignement',
  'settings.intelEnable': 'Activer la cartographie de renseignement',
  'settings.intelHint': 'Tes bots explorent la carte de la région choisie et alimentent le réseau Intelligence. Chaque case scannée te rapporte des jetons pour payer des recherches.',
  'settings.intelRegion': 'Région à cartographier',
  'settings.intelRegionHint': '3 à 5 chiffres (ex. 4347). En général, ta propre région.',
  'settings.intelCheckRegion': 'Vérifier la région',
  'settings.intelChecking': 'Vérification de la région…',
  'settings.intelRegionReady': 'La région {region} est déjà dans le réseau ({total} cases). Prête à cartographier.',
  'settings.intelRegionSeeded': 'Région {region} ajoutée au réseau ({total} cases). Tes bots peuvent commencer à la cartographier.',
  'settings.intelRegionInvalid': 'Région non valide : utilise 3 à 5 chiffres.',
  'settings.intelRegionError': 'Impossible de vérifier la région. Réessaie.',
  'settings.intelCellsPerPass': 'Cases par passage',
  'settings.intelCellsPerPassHint': 'Nombre de cases que le bot scanne à chaque tour de cartographie avant de reprendre ses autres tâches.',
  'settings.game': 'Jeu',
  'settings.gameLanguage': 'Langue du jeu',
  'settings.gameLanguageEn': 'Anglais (par defaut)',
  'settings.gameLanguageEs': 'Espagnol',
  'settings.gameLanguageHint': 'C\'est la langue du JEU dans BlueStacks (pas celle de ce site). Le bot ne sait lire le jeu qu\'en anglais ou en espagnol : règle-la dans Paramètres du jeu → Langue et choisis la même ici.',
  'settings.cryptids': 'Cryptides d\'abord (événements)',
  'settings.cryptidsHint': 'Quand l\'événement Berserk Cryptid est actif et que tu as une Corne de Cryptide, la chasse la dépense sur un cryptide ; sans événement ni cornes, chasse normale (tuer des bêtes fait tomber les cornes).',
  'status.hunting_cryptid': 'Recherche d\'un cryptide de l\'événement…',

  'subscribe.title': 'Essayez White Bot gratuitement 5 jours',
  'subscribe.body': 'Executez White Bot sur votre propre PC (Windows + BlueStacks) et controlez-le depuis ce site.',
  'subscribe.trial': 'ESSAI',
  'subscribe.trialValue': '5 jours gratuits',
  'subscribe.after': 'ENSUITE',
  'subscribe.afterValue': '11,99 € / mois',
  'subscribe.includes': 'INCLUS',
  'subscribe.includesValue': 'Collecte · Troupes · Chasse · Soins · Bouclier · Aide d\'alliance · Tâches quotidiennes',
  'subscribe.cancel': 'ANNULEZ',
  'subscribe.cancelValue': 'Quand vous voulez',
  'subscribe.cta': 'Demarrer l\'essai gratuit (5 jours)',
  'subscribe.ctaOpening': 'Ouverture du paiement…',
  'subscribe.cardNote': 'Pas de carte pour l\'essai. Tu ne paies que si tu décides de t\'abonner à la fin.',
  'subscribe.trialCta': 'Commencer l\'essai gratuit de 5 jours — sans carte',
  'subscribe.trialStarting': 'Activation de ton essai…',
  'subscribe.trialError': 'Impossible d\'activer l\'essai. Réessaie.',
  'subscribe.trialUsed': 'L\'essai gratuit a déjà été utilisé sur ce compte.',
  'subscribe.payCta': 'S\'abonner — 11,99 €/mois',
  'subscribe.endedTitle': 'Ton accès est terminé',
  'subscribe.endedBody': 'Ton essai ou ton abonnement est terminé. Abonne-toi pour continuer à utiliser White Bot sur ton PC — ta configuration est conservée.',
  'subscribe.checkoutError': 'Erreur au demarrage du paiement',
  'subscribe.hostedNote': 'Vous utilisez le service hosted (sur nos serveurs) ? C\'est un autre produit :',
  'subscribe.hostedLink': 'aller sur whitebot.app',

  // Bêta gratuite jusqu'au 15 août 2026 (domain/beta.ts)
  'beta.banner': 'Bêta gratuite',
  'subscribe.betaTitle': 'White Bot, gratuit pendant la bêta',
  'subscribe.betaTrial': 'BÊTA',
  'subscribe.betaTrialValue': 'Gratuit jusqu\'au 15 août 2026',
  'subscribe.betaTrialCta': 'Commencer la bêta gratuite',
  'subscribe.betaCardNote': 'Pas de carte. White Bot est gratuit pendant la bêta, jusqu\'au 15 août 2026.',
  'subscribe.betaEndedNote': 'Nous sommes en bêta gratuite. Si tu n\'as pas accès, écris-nous via le chat du support.',

  'tutorial.title': 'Premiers pas',
  'tutorial.intro': 'Trois etapes pour avoir votre bot sur votre PC, controle depuis ici.',
  'tutorial.step1Title': '1. Installez le client',
  'tutorial.step1Body': 'Telechargez le client et installez-le sur votre PC avec BlueStacks. Le guide vous accompagne pas a pas.',
  'tutorial.step2Title': '2. Activez-le avec votre email',
  'tutorial.step2Body': 'Ouvrez le client et saisissez votre email. Une fois connecte, vous verrez "client connecte" ici.',
  'tutorial.step3Title': '3. Controlez le bot depuis ici',
  'tutorial.step3Body': 'Choisissez modules et reglages, lancez une action et suivez la capture et l\'etat sur ce panneau.',
  'tutorial.dontShowAgain': 'Ne plus afficher',
  'tutorial.notNow': 'Pas maintenant',
  'tutorial.gotIt': 'Compris',

  'pills.license': 'licence : {label}',

  'dashboard.refreshCapture': 'Actualiser la capture',
  'dashboard.refreshCapturePending': 'Capture en cours…',
  'dashboard.refreshCaptureTimeout': "Le client n'a pas répondu à la demande de capture. Vérifie qu'il est ouvert sur ton PC.",

  'state.idle': 'inactif',
  'state.running': 'en marche',
  'state.waiting': 'en attente',
  'state.starting': 'démarrage',
  'state.verifying': 'vérification',
  'state.training': 'entraînement',
  'state.collecting': 'récolte',
  'state.hunting': 'chasse',
  'state.helping': 'entraide',
  'state.healing': 'soins',
  'state.recovering': 'récupération',
  'state.reconnecting': 'reconnexion',
  'state.shielded': 'bouclier actif',
  'state.error': 'erreur',

  'status.no_client': 'Aucun client connecté.',
  'status.idle': 'En attente (arrêté depuis le web).',
  'status.running': 'En cours : {modules}',
  'status.running_none': 'En cours (aucun module actif).',
  'status.shielded': "Auto Bouclier a activé la protection : bot arrêté. Appuie sur Arrêter puis Démarrer pour reprendre.",
  'shieldAlarm.eyebrow': 'Auto Bouclier',
  'shieldAlarm.title': "Nous t'avons protégé d'une attaque",
  'shieldAlarm.body': "Nous avons détecté une attaque contre {name} et activé ton bouclier automatiquement. Tes troupes ont été rappelées et ta colonie est protégée. Le bot s'est arrêté par sécurité : appuie sur Démarrer quand tu veux reprendre.",
  'shieldAlarm.time': "Heure de l'attaque",
  'shieldAlarm.close': 'Compris',
  'status.starting': "Vérification de démarrage : nettoyage de l'écran et vérification de la colonie…",
  'status.waiting': 'Rien à faire. Prochaine vérification dans {time}.',
  'status.backoff_wait': 'Récupération en cours ; nouvel essai dans {time}.',
  'status.verifying_training': "Vérification des files d'entraînement…",
  'status.verifying_marches': 'Vérification des files de marche…',
  'status.verifying_hunt': 'Vérification des marches de chasse…',
  'status.training_collect': 'Collecte des troupes terminées : {units}',
  'langWarn.eyebrow': 'Langue du jeu',
  'langWarn.title': 'Mettez le jeu en anglais',
  'langWarn.body': "White Bot ne peut pas lire votre jeu : sa langue n'est pas prise en charge. Dans Whiteout Survival, ouvrez Paramètres → Language et choisissez English. Le bot reprend tout seul dès le changement.",
  'langWarn.close': 'Compris',
  'fleet.title': 'Mes bots',
  'fleet.counter': '{n}/{max} bots',
  'fleet.startAll': 'Tout démarrer',
  'fleet.stopAll': 'Tout arrêter',
  'fleet.delete': 'Supprimer',
  'fleet.deleteConfirmTitle': 'Supprimer {name} ?',
  'fleet.deleteConfirmBody': 'Cela libère une place de la limite de ton compte. Si le client de ce bot est encore ouvert sur ton PC, il réapparaîtra à son prochain rapport.',
  'fleet.deleteConfirm': 'Supprimer le bot',
  'fleet.deleteCancel': 'Annuler',
  'fleet.back': 'Tous les bots',
  'fleet.empty': 'Pas encore de bots. Ouvre White Bot Client sur ton PC (une console par instance BlueStacks) et ils apparaîtront ici tout seuls.',
  'fleet.defaultName': 'Bot',
  'fleet.lastReport': 'Dernier rapport',
  'status.unsupported_language': 'Langue du jeu non prise en charge — passez-la en anglais',
  'status.language_autofix': "Langue non prise en charge : passage automatique du jeu en anglais…",
  'status.dailies': 'Récupération des récompenses quotidiennes…',
  'status.intel_missions': 'Missions de renseignement…',
  'status.intel_map_scanning': 'Renseignement : {cells} cases scannées (+{tokens} jetons)',
  'status.intel_map_region_done': 'Renseignement : région à jour',
  'status.intel_map_detector_missing': 'Renseignement : détecteur indisponible',
  'status.intel_map_region_missing': 'Renseignement : configure la région dans les Réglages',
  'state.intel': 'Renseignement',
  'state.dailies': 'Récompenses quotidiennes',
  'status.language_fixed': "Langue passée en anglais ; en attente du redémarrage du jeu…",
  'state.fixing_language': 'Changement de langue',
  'state.blocked_language': 'Langue non prise en charge',
  'status.training_units': "Entraînement d'unités : {units} ({percent}%)",
  'status.collecting_send': 'Envoi d\'une marche de récolte : {resource} (niveau {level})',
  'status.hunting_send': "Envoi d'une attaque de chasse : {type} niveau {level}",
  'status.helping': "Vérification de l'Entraide d'alliance…",
  'status.healing': 'Vérification des Soins auto…',
  'status.recovering_attempt': 'Récupération ({attempt}/{max}) ; nouvel essai dans {time}…',
  'status.recovered_ui': 'Écran rétabli après un échec ({task}). Reprise…',
  'status.relaunching': 'Relance de BlueStacks/du jeu après des échecs répétés…',
  'status.relaunched': 'Jeu relancé après un échec. Reprise dans {time}.',
  'status.hand_guide': 'Main-guide du tutoriel détectée : appui là où elle pointe…',
  'status.reconnecting': 'Session ouverte sur un autre appareil : appui sur Reconnecter…',
  'status.backoff_init_error': "Erreur d'initialisation ({detail}). Nouvel essai automatique dans {time}.",
  'status.backoff_no_adb': 'Pas de connexion à BlueStacks (ADB). Vérifie que BlueStacks est ouvert. Nouvel essai dans {time}.',
  'status.backoff_failure': 'Échec répété ({task}). Nouvel essai automatique dans {time}.',
  'status.backoff_blocked': 'Écran bloqué ({kind}). Nouvel essai automatique dans {time}.',

  'statusTask.verify_training': "vérification des files d'entraînement",
  'statusTask.collect_completed': 'collecte des troupes terminées',
  'statusTask.train_flow': "flux d'entraînement",
  'statusTask.return_after_training': 'retour à la colonie après entraînement',
  'statusTask.verify_marches': 'vérification des marches',
  'statusTask.collect_flow': 'flux de récolte',
  'statusTask.hunt_capture': "capture de l'état de chasse",
  'statusTask.verify_hunt': 'vérification des marches de chasse',
  'statusTask.hunt_vigor': 'lecture de la vigueur de chasse',
  'statusTask.hunt_flow': 'flux de chasse',
  'statusTask.auto_heal': 'soins auto',
  'statusTask.startup_return': 'retour à la colonie au démarrage',
  'statusTask.startup_screen': 'écran non reconnu au démarrage',
  'statusTask.unexpected': 'erreur inattendue',

  'intelligence.title': 'Intelligence',
  'intelligence.intro': 'Consulte les joueurs et alliances recensés par le réseau White Bot. {limit} recherches par jour.',
  'intelligence.region': 'Région',
  'intelligence.regionPlaceholder': 'Région (ex. 4347)',
  'intelligence.searchPlayer': 'Rechercher joueur ou alliance',
  'intelligence.searchPlaceholder': 'Joueur ou alliance (vide = toute la région)',
  'intelligence.regionSearchTitle': 'Toute la région {region}',
  'intelligence.errorUnknownRegion': 'Cette région n’est pas encore dans le réseau (vérifie le numéro).',
  'intelligence.quotaRemainingValue': '{remaining} sur {limit}',
  'intelligence.request': 'Rechercher',
  'intelligence.requesting': 'Recherche…',
  'intelligence.quotaRemaining': 'Recherches restantes aujourd\'hui',
  'intelligence.minChars': 'Saisis au moins 3 caractères, ou laisse vide pour lister toute la région.',
  'intelligence.regionRequired': 'Indique la région où chercher (ex. 4347).',
  'intelligence.loading': 'Chargement des recherches…',
  'intelligence.empty': 'Aucune recherche pour l\'instant.',
  'intelligence.delete': 'Supprimer la recherche',
  'intelligence.power': 'Puissance',
  'intelligence.coordinates': 'Coordonnées',
  'intelligence.notFound': 'Aucune correspondance.',
  'intelligence.noResult': 'Résultat en attente (pas encore de données dans cette région).',
  'intelligence.statusPending': 'En file',
  'intelligence.statusProcessing': 'Recherche',
  'intelligence.statusCompleted': 'Terminée',
  'intelligence.statusNotFound': 'Introuvable',
  'intelligence.statusFailed': 'Impossible d\'obtenir le résultat',
  'intelligence.errorQuota': 'Tu as épuisé tes recherches du jour. Reviens demain.',
  'intelligence.errorPending': 'Cette recherche est déjà en cours.',
  'intelligence.errorNoRegion': 'Indique une région valide où chercher.',
  'intelligence.errorGeneric': 'La recherche n\'a pas pu être effectuée. Réessaie.',
  'intelligence.search': 'Recherche',
  'intelligence.resultsFound': '{count} résultats',
  'intelligence.showMore': 'Voir {count} de plus',
  'intelligence.showLess': 'Voir moins',
  'intelligence.sortResults': 'Trier les résultats',
  'intelligence.sortPower': 'Puissance',
  'intelligence.sortName': 'Nom',
  'intelligence.lastSeen': 'Vu',
  'intelligence.deleteConfirm': 'Supprimer « {name} » du panneau ? La recherche comptera quand même dans ta limite quotidienne.',
  // Économie de jetons + progression de la cartographie (0.6.4, doc 17)
  'intelligence.tokensChip': '{n} jetons',
  'intelligence.freeChip': '{n} gratuite(s) aujourd\'hui',
  'intelligence.freeTodayLabel': 'Recherche gratuite du jour',
  'intelligence.tokensBalance': 'Solde de jetons',
  'intelligence.costsLine': 'Coût en jetons : recherche {search} · région entière {region}',
  'intelligence.noTokensCta': 'Plus de recherche gratuite ni de jetons : active la cartographie de renseignement dans les réglages et tes bots gagneront des jetons en scannant la carte.',
  'intelligence.errorTokens': 'Pas assez de jetons pour cette recherche. Active la cartographie pour en gagner.',
  'intelligence.mappingTitle': 'Progression de la cartographie',
  'intelligence.mappingCells': '{scanned} sur {total} cases',
  'intelligence.mappingToday': 'Scannées aujourd\'hui par tes bots',
  'intelligence.mappingEarnedToday': 'Jetons gagnés aujourd\'hui',
  'shieldWatch.toggle': 'Surveiller le bouclier d\'un joueur (ce bot arrête de cartographier)',
  'shieldWatch.title': 'Shield Monitor',
  'shieldWatch.historyTitle': 'Historique de la surveillance',
  'activity.title': 'Activité de renseignement',
  'activity.showAll': 'Tout afficher ({count})',
  'activity.showLess': 'Afficher moins',
  'activity.protected': 'Attaque détectée : nous t\'avons protégé',
  'activity.attackHalted': 'Attaque détectée : bouclier NON confirmé',
  'shieldWatch.captureOpen': 'Voir la capture de ce moment',
  'shieldWatch.captureLoading': 'Chargement de la capture…',
  'shieldWatch.captureAlt': 'Capture du moment où le bot a décidé',
  'shieldWatch.captureGone': 'Cette capture n\'est plus là : seules les 5 dernières de chaque bot sont conservées.',
  'shieldWatch.captureClose': 'Fermer',
  'shieldWatch.help': 'Saisis les coordonnées du château à surveiller. Le bot vérifiera régulièrement s\'il a encore un bouclier et t\'enverra un email quand il tombera.',
  'shieldWatch.interval': 'Toutes les',
  'shieldWatch.region': 'Région',
  'shieldWatch.needCoords': 'Indique les coordonnées X et Y du joueur à surveiller.',
  'shieldWatch.idleNote': 'Les vérifications se font pendant les temps libres du bot ; avec une ferme chargée, elles peuvent s\'espacer.',
  'shieldWatch.stateShielded': 'La cible a TOUJOURS son bouclier',
  'shieldWatch.stateConfirming': 'Sans bouclier : confirmation…',
  'shieldWatch.stateDown': 'Bouclier TOMBÉ !',
  'shieldWatch.stateMoved': 'La cible n\'est plus à ces coordonnées',
  'shieldWatch.stateNoDetector': 'Surveillance impossible : détecteur indisponible',
  'intelligence.mappingNoRegion': 'Choisis et vérifie une région dans Réglages → Cartographie de renseignement pour voir sa progression.',

  'support.title': 'Support',
  'support.subtitle': 'Service client — on te répond ici même.',
  'support.open': 'Ouvrir le chat du support',
  'support.close': 'Fermer le chat du support',
  'support.empty': 'Besoin d\'aide ? Décris ton problème (tu peux joindre une capture) et on te répondra dans ce chat.',
  'support.placeholder': 'Écris ton message…',
  'support.send': 'Envoyer',
  'support.sending': 'Envoi…',
  'support.attach': 'Joindre une capture',
  'support.attachRemove': 'Retirer l\'image',
  'support.attachTooBig': 'L\'image dépasse 5 Mo.',
  'support.attachNotImage': 'Seules les images peuvent être jointes.',
  'support.imageAlt': 'Capture jointe',
  'support.you': 'Toi',
  'support.agent': 'Support',
  'support.sendError': 'Le message n\'a pas pu être envoyé. Réessaie.',
  'support.loading': 'Chargement de la conversation…',
  'support.unreadLabel': '{count} nouveaux messages du support'
};

const de: Record<TranslationKey, string> = {
  'app.account': 'Konto',
  'app.configuration': 'Konfiguration',
  'app.configurationUnavailable': 'Konfiguration nicht verfugbar',
  'app.faq': 'FAQ',
  'app.faqButton': 'Haufige Fragen',
  'app.language': 'Sprache',
  'app.license': 'Lizenz',
  'app.manageBilling': 'Abo verwalten',
  'app.subscribe': 'Abonnieren',
  'app.openAccountMenu': 'Kontomenu offnen',
  'app.signOut': 'Abmelden',
  'app.billingError': 'Das Abrechnungsportal konnte nicht geoffnet werden.',
  'app.billingNoStripe': 'Deine Lizenz wird nicht über Stripe verwaltet (manuell gewährter Zugang): es gibt keine Zahlungen zu verwalten.',
  'app.product': 'White Bot Desktop',
  'app.guide': 'Anleitung',
  'app.guideButton': 'Installationsanleitung ansehen',

  'auth.title': 'White Bot',
  'auth.subtitle': 'Desktop-Plan · 11,99 €/Monat · 5 Tage testen',
  'auth.betaSubtitle': 'Kostenlose Beta',
  'auth.intro': 'Melde dich an, um deinen Bot zu verwalten.',
  'auth.email': 'E-Mail',
  'auth.placeholderEmail': 'du@email.com',
  'auth.sendLink': 'Zugangslink senden',
  'auth.googleLogin': 'Mit Google anmelden',
  'auth.checkEmail': 'Prufe deine E-Mail: Wir haben dir einen Zugangslink geschickt.',
  'auth.loadingSession': 'Sitzung wird geladen…',

  'entitlement.checking': 'Abo wird gepruft…',
  'entitlement.active': 'aktiv',
  'entitlement.trialing': 'Testphase',
  'entitlement.past_due': 'Zahlung ausstehend',
  'entitlement.canceled': 'gekundigt',
  'entitlement.inactive': 'inaktiv',

  'pills.connected': 'Client verbunden',
  'pills.disconnected': 'Client getrennt',

  'dashboard.actions': 'Aktionen',
  'dashboard.autoBot': 'Auto Bot',
  'dashboard.collect': 'Sammeln',
  'dashboard.trainTroops': 'Truppen trainieren',
  'dashboard.autoHunt': 'Auto Hunt',
  'dashboard.stopBot': 'Bot stoppen',
  'dashboard.screenshot': 'Bildschirmfoto',
  'dashboard.mockView': 'Simulierte Ansicht — der Client hat noch keine Aufnahmen gesendet',
  'dashboard.marches': 'Marsche',
  'dashboard.training': 'Training',
  'dashboard.noRecentVerification': 'Keine aktuelle Uberprufung',
  'dashboard.queue': 'Slot',
  'dashboard.state': 'Status',
  'dashboard.activity': 'Aktivitat',
  'dashboard.lastReport': 'Letzter Bericht',
  'dashboard.clientVersion': 'Client-Version',
  'dashboard.never': 'nie',
  'dashboard.disconnectedNotice': 'Der Client meldet sich seit einer Weile nicht. Prufe, ob er auf deinem PC mit BlueStacks geoffnet ist.',
  'dashboard.settingsSaved': 'Einstellungen gespeichert.',
  'dashboard.client': 'Client',
  'dashboard.clientBody': 'Lade den Client herunter und installiere ihn auf deinem PC (Windows + BlueStacks). Er wird mit deinem Konto aktiviert.',
  'dashboard.clientDownload': 'Client herunterladen (verfugbar bei GA)',
  'dashboard.clientDownloadReady': 'Client herunterladen (Beta)',
  'dashboard.clientSmartscreen': 'Download als ZIP: entpacken und den Installer starten. Unsignierte Beta: Wenn Browser oder Windows warnen, wahle "Beibehalten" und "Weitere Informationen → Trotzdem ausfuhren".',

  'op.workingEyebrow': 'Vorgang lauft',
  'op.successEyebrow': 'Abgeschlossen',
  'op.errorEyebrow': 'Keine Bestatigung',
  'op.warningEyebrow': 'Achtung',
  'op.startTitle': '{action} wird gestartet',
  'op.startBody': 'Befehl gesendet. Der Client auf deinem PC ubernimmt ihn in wenigen Sekunden.',
  'op.stopTitle': 'Bot wird gestoppt',
  'op.stopBody': 'Befehl gesendet. Der Bot beendet den aktuellen Schritt und stoppt dann.',
  'op.successStartTitle': 'Bot lauft',
  'op.successStartBody': 'Der Client hat die Ausfuhrung bestatigt.',
  'op.successStopTitle': 'Bot gestoppt',
  'op.successStopBody': 'Der Client hat den Stopp bestatigt.',
  'op.timeoutTitle': 'Der Client hat nicht bestatigt',
  'op.timeoutBody': 'Der Befehl ist gespeichert und wird angewendet, sobald der Client verbindet. Prufe, ob der Client auf deinem PC geoffnet ist.',
  'op.disconnectedTitle': 'Client getrennt',
  'op.disconnectedBody': 'Dein Bot meldet sich gerade nicht. Offne den Client auf deinem PC (mit BlueStacks) und versuche es erneut. Du kannst den Befehl trotzdem senden.',
  'op.sendAnyway': 'Trotzdem senden',
  'op.close': 'Schliessen',
  'op.cancel': 'Abbrechen',

  'settings.level': 'Stufe',
  'settings.marches': 'Marsche',
  'settings.amount': 'Menge pro Charge',
  'settings.type': 'Typ',
  'settings.beasts': 'Bestien (10 Ausdauer)',
  'settings.terror': 'Polarschrecken (25 Ausdauer)',
  'settings.timer': 'Rally-Wartezeit',
  'settings.up': 'Erhohen',
  'settings.down': 'Verringern',
  'settings.resources.iron': 'Eisen',
  'settings.resources.coal': 'Kohle',
  'settings.resources.wood': 'Holz',
  'settings.resources.meat': 'Fleisch',
  'settings.units.infantry': 'Infanterie',
  'settings.units.lancer': 'Lanzentrager',
  'settings.units.marksman': 'Schutzen',
  'settings.autoHelp': 'Auto-Hilfe',
  'settings.autoHeal': 'Auto-Heilung',
  'settings.autoShield': 'Auto-Schild',
  'settings.tasks': 'Aufgaben',
  'settings.tasksEnable': 'Automatische Aufgaben aktivieren',
  'settings.tasksHint': 'Tägliche Belohnungen und Missionen: Post, Gratis-Helden, Leerlauf-Truhe und Aufklärungsmissionen. Automatisch nach Zeitplan.',
  'settings.taskMail': 'Post (abholen)',
  'settings.taskHeroes': 'Gratis-Helden',
  'settings.taskIdle': 'Leerlauf-Truhe',
  'settings.taskIntel': 'Aufklärungsmissionen',
  // Aufklärungskartierung (0.6.4, doc 17)
  'settings.intelMapping': 'Aufklärungskartierung',
  'settings.intelEnable': 'Aufklärungskartierung aktivieren',
  'settings.intelHint': 'Deine Bots erkunden die Karte der gewählten Region und speisen das Intelligence-Netzwerk. Jedes gescannte Feld bringt dir Tokens für Suchen.',
  'settings.intelRegion': 'Zu kartierende Region',
  'settings.intelRegionHint': '3 bis 5 Ziffern (z. B. 4347). Normalerweise deine eigene Region.',
  'settings.intelCheckRegion': 'Region prüfen',
  'settings.intelChecking': 'Region wird geprüft…',
  'settings.intelRegionReady': 'Region {region} ist bereits im Netzwerk ({total} Felder). Bereit zur Kartierung.',
  'settings.intelRegionSeeded': 'Region {region} zum Netzwerk hinzugefügt ({total} Felder). Deine Bots können sie jetzt kartieren.',
  'settings.intelRegionInvalid': 'Ungültige Region: verwende 3 bis 5 Ziffern.',
  'settings.intelRegionError': 'Die Region konnte nicht geprüft werden. Versuche es erneut.',
  'settings.intelCellsPerPass': 'Felder pro Durchgang',
  'settings.intelCellsPerPassHint': 'Wie viele Felder der Bot pro Kartierungsrunde scannt, bevor er mit seinen anderen Aufgaben weitermacht.',
  'settings.game': 'Spiel',
  'settings.gameLanguage': 'Spielsprache',
  'settings.gameLanguageEn': 'Englisch (Standard)',
  'settings.gameLanguageEs': 'Spanisch',
  'settings.gameLanguageHint': 'Das ist die Sprache des SPIELS in BlueStacks (nicht die dieser Website). Der Bot kann das Spiel nur auf Englisch oder Spanisch lesen: stell sie in den Spiel-Einstellungen → Sprache ein und wähle hier dieselbe.',
  'settings.cryptids': 'Kryptiden zuerst (Events)',
  'settings.cryptidsHint': 'Ist das Berserk-Cryptid-Event aktiv und hast du ein Kryptiden-Horn, gibt die Jagd es für einen Kryptiden aus; ohne Event oder Hörner jagt der Bot wie üblich (Bestien töten bringt die Hörner).',
  'status.hunting_cryptid': 'Suche nach einem Event-Kryptiden…',

  'subscribe.title': 'Teste White Bot 5 Tage kostenlos',
  'subscribe.body': 'Fuhre White Bot auf deinem eigenen PC aus (Windows + BlueStacks) und steuere ihn uber diese Website.',
  'subscribe.trial': 'TEST',
  'subscribe.trialValue': '5 Tage kostenlos',
  'subscribe.after': 'DANACH',
  'subscribe.afterValue': '11,99 € / Monat',
  'subscribe.includes': 'ENTHALT',
  'subscribe.includesValue': 'Sammeln · Truppen · Jagd · Heilung · Schild · Allianzhilfe · Tägliche Aufgaben',
  'subscribe.cancel': 'KUNDIGEN',
  'subscribe.cancelValue': 'Jederzeit',
  'subscribe.cta': 'Kostenlosen Test starten (5 Tage)',
  'subscribe.ctaOpening': 'Zahlung wird geoffnet…',
  'subscribe.cardNote': 'Keine Karte für die Testphase nötig. Du zahlst nur, wenn du dich am Ende für ein Abo entscheidest.',
  'subscribe.trialCta': 'Kostenlose 5-Tage-Testphase starten — ohne Karte',
  'subscribe.trialStarting': 'Testphase wird aktiviert…',
  'subscribe.trialError': 'Die Testphase konnte nicht aktiviert werden. Versuche es erneut.',
  'subscribe.trialUsed': 'Die kostenlose Testphase wurde auf diesem Konto bereits genutzt.',
  'subscribe.payCta': 'Abonnieren — 11,99 €/Monat',
  'subscribe.endedTitle': 'Dein Zugang ist abgelaufen',
  'subscribe.endedBody': 'Deine Testphase oder dein Abo ist beendet. Abonniere, um White Bot weiter auf deinem PC zu nutzen — deine Einstellungen bleiben erhalten.',
  'subscribe.checkoutError': 'Fehler beim Start der Zahlung',
  'subscribe.hostedNote': 'Nutzt du den Hosted-Dienst (lauft auf unseren Servern)? Das ist ein anderes Produkt:',
  'subscribe.hostedLink': 'zu whitebot.app',

  // Kostenlose Beta bis 15. August 2026 (domain/beta.ts)
  'beta.banner': 'Kostenlose Beta',
  'subscribe.betaTitle': 'White Bot — kostenlos während der Beta',
  'subscribe.betaTrial': 'BETA',
  'subscribe.betaTrialValue': 'Kostenlos bis 15. Aug 2026',
  'subscribe.betaTrialCta': 'Kostenlose Beta starten',
  'subscribe.betaCardNote': 'Keine Karte nötig. White Bot ist während der Beta kostenlos, bis zum 15. August 2026.',
  'subscribe.betaEndedNote': 'Wir sind in der kostenlosen Beta. Wenn du keinen Zugang hast, schreib uns im Support-Chat.',

  'tutorial.title': 'Erste Schritte',
  'tutorial.intro': 'Drei Schritte, bis dein Bot auf deinem PC lauft — gesteuert von hier.',
  'tutorial.step1Title': '1. Client installieren',
  'tutorial.step1Body': 'Lade den Client herunter und installiere ihn zusammen mit BlueStacks. Die Anleitung begleitet dich Schritt fur Schritt.',
  'tutorial.step2Title': '2. Mit deiner E-Mail aktivieren',
  'tutorial.step2Body': 'Offne den Client und gib deine E-Mail ein. Sobald er verbindet, siehst du hier "Client verbunden".',
  'tutorial.step3Title': '3. Bot von hier steuern',
  'tutorial.step3Body': 'Wahle Module und Einstellungen, starte eine Aktion und verfolge Aufnahme und Status in diesem Panel.',
  'tutorial.dontShowAgain': 'Nicht mehr anzeigen',
  'tutorial.notNow': 'Jetzt nicht',
  'tutorial.gotIt': 'Verstanden',

  'pills.license': 'Lizenz: {label}',

  'dashboard.refreshCapture': 'Bild aktualisieren',
  'dashboard.refreshCapturePending': 'Aufnahme läuft…',
  'dashboard.refreshCaptureTimeout': 'Der Client hat nicht auf die Aufnahme-Anfrage geantwortet. Prüfe, ob er auf deinem PC geöffnet ist.',

  'state.idle': 'inaktiv',
  'state.running': 'läuft',
  'state.waiting': 'wartet',
  'state.starting': 'startet',
  'state.verifying': 'prüft',
  'state.training': 'trainiert',
  'state.collecting': 'sammelt',
  'state.hunting': 'jagt',
  'state.helping': 'hilft',
  'state.healing': 'heilt',
  'state.recovering': 'erholt sich',
  'state.reconnecting': 'verbindet neu',
  'state.shielded': 'Schild aktiv',
  'state.error': 'Fehler',

  'status.no_client': 'Kein Client verbunden.',
  'status.idle': 'Bereit (über das Web gestoppt).',
  'status.running': 'Läuft: {modules}',
  'status.running_none': 'Läuft (keine aktiven Module).',
  'status.shielded': 'Auto-Schild hat den Schutz aktiviert: Bot gestoppt. Drücke Stopp und dann erneut Start, um fortzufahren.',
  'shieldAlarm.eyebrow': 'Auto-Schild',
  'shieldAlarm.title': 'Wir haben dich vor einem Angriff geschützt',
  'shieldAlarm.body': 'Wir haben einen Angriff auf {name} erkannt und deinen Schild automatisch aktiviert. Deine Truppen wurden zurückgezogen und deine Kolonie ist geschützt. Der Bot wurde zur Sicherheit gestoppt – drücke Start, um fortzufahren.',
  'shieldAlarm.time': 'Zeitpunkt des Angriffs',
  'shieldAlarm.close': 'Verstanden',
  'status.starting': 'Startprüfung: Bildschirm wird bereinigt und Kolonie geprüft…',
  'status.waiting': 'Nichts zu tun. Nächste Prüfung in {time}.',
  'status.backoff_wait': 'Wiederherstellung läuft; neuer Versuch in {time}.',
  'status.verifying_training': 'Trainingswarteschlangen werden geprüft…',
  'status.verifying_marches': 'Marschwarteschlangen werden geprüft…',
  'status.verifying_hunt': 'Jagdmärsche werden geprüft…',
  'status.training_collect': 'Fertige Truppen werden abgeholt: {units}',
  'langWarn.eyebrow': 'Spielsprache',
  'langWarn.title': 'Stelle das Spiel auf Englisch',
  'langWarn.body': 'White Bot kann dein Spiel nicht lesen: Die Sprache wird nicht unterstützt. Öffne in Whiteout Survival Einstellungen → Language und wähle English. Der Bot macht nach dem Wechsel von selbst weiter.',
  'langWarn.close': 'Verstanden',
  'fleet.title': 'Meine Bots',
  'fleet.counter': '{n}/{max} Bots',
  'fleet.startAll': 'Alle starten',
  'fleet.stopAll': 'Alle stoppen',
  'fleet.delete': 'Löschen',
  'fleet.deleteConfirmTitle': '{name} löschen?',
  'fleet.deleteConfirmBody': 'Das gibt einen Platz deines Kontolimits frei. Läuft der Client dieses Bots noch auf deinem PC, erscheint er beim nächsten Bericht wieder.',
  'fleet.deleteConfirm': 'Bot löschen',
  'fleet.deleteCancel': 'Abbrechen',
  'fleet.back': 'Alle Bots',
  'fleet.empty': 'Noch keine Bots. Öffne White Bot Client auf deinem PC (eine Konsole pro BlueStacks-Instanz) und sie erscheinen hier von selbst.',
  'fleet.defaultName': 'Bot',
  'fleet.lastReport': 'Letzter Bericht',
  'status.unsupported_language': 'Spielsprache nicht unterstützt — stelle sie auf Englisch',
  'status.language_autofix': 'Sprache nicht unterstützt: Spiel wird automatisch auf Englisch umgestellt…',
  'status.dailies': 'Tägliche Belohnungen werden abgeholt…',
  'status.intel_missions': 'Aufklärungsmissionen…',
  'status.intel_map_scanning': 'Aufklärung: {cells} Felder gescannt (+{tokens} Tokens)',
  'status.intel_map_region_done': 'Aufklärung: Region auf dem neuesten Stand',
  'status.intel_map_detector_missing': 'Aufklärung: Detektor nicht verfügbar',
  'status.intel_map_region_missing': 'Aufklärung: lege die Region in den Einstellungen fest',
  'state.intel': 'Aufklärung',
  'state.dailies': 'Tägliche Belohnungen',
  'status.language_fixed': 'Sprache auf Englisch umgestellt; warte auf den Neustart des Spiels…',
  'state.fixing_language': 'Sprache wird geändert',
  'state.blocked_language': 'Sprache nicht unterstützt',
  'status.training_units': 'Einheiten werden trainiert: {units} ({percent}%)',
  'status.collecting_send': 'Sammelmarsch wird gesendet: {resource} (Stufe {level})',
  'status.hunting_send': 'Jagdangriff wird gesendet: {type} Stufe {level}',
  'status.helping': 'Allianz-Autohilfe wird geprüft…',
  'status.healing': 'Auto-Heilung wird geprüft…',
  'status.recovering_attempt': 'Wiederherstellung ({attempt}/{max}); neuer Versuch in {time}…',
  'status.recovered_ui': 'Bildschirm nach einem Fehler wiederhergestellt ({task}). Weiter geht’s…',
  'status.relaunching': 'BlueStacks/Spiel wird nach wiederholten Fehlern neu gestartet…',
  'status.relaunched': 'Spiel nach einem Fehler neu gestartet. Fortsetzung in {time}.',
  'status.hand_guide': 'Tutorial-Hand erkannt: Tippen, wohin sie zeigt…',
  'status.reconnecting': 'Sitzung auf einem anderen Gerät geöffnet: „Erneut verbinden“ wird gedrückt…',
  'status.backoff_init_error': 'Initialisierungsfehler ({detail}). Automatischer Neuversuch in {time}.',
  'status.backoff_no_adb': 'Keine Verbindung zu BlueStacks (ADB). Prüfe, ob BlueStacks geöffnet ist. Neuer Versuch in {time}.',
  'status.backoff_failure': 'Wiederholter Fehler ({task}). Automatischer Neuversuch in {time}.',
  'status.backoff_blocked': 'Bildschirm blockiert ({kind}). Automatischer Neuversuch in {time}.',

  'statusTask.verify_training': 'Prüfung der Trainingswarteschlangen',
  'statusTask.collect_completed': 'Abholen fertiger Truppen',
  'statusTask.train_flow': 'Trainingsablauf',
  'statusTask.return_after_training': 'Rückkehr zur Kolonie nach dem Training',
  'statusTask.verify_marches': 'Marschprüfung',
  'statusTask.collect_flow': 'Sammelablauf',
  'statusTask.hunt_capture': 'Aufnahme des Jagdstatus',
  'statusTask.verify_hunt': 'Prüfung der Jagdmärsche',
  'statusTask.hunt_vigor': 'Ablesen der Jagd-Energie',
  'statusTask.hunt_flow': 'Jagdablauf',
  'statusTask.auto_heal': 'Auto-Heilung',
  'statusTask.startup_return': 'Rückkehr zur Kolonie beim Start',
  'statusTask.startup_screen': 'unbekannter Bildschirm beim Start',
  'statusTask.unexpected': 'unerwarteter Fehler',

  'intelligence.title': 'Intelligence',
  'intelligence.intro': 'Sieh Spieler und Allianzen nach, die das White-Bot-Netzwerk erfasst hat. {limit} Suchen pro Tag.',
  'intelligence.region': 'Region',
  'intelligence.regionPlaceholder': 'Region (z. B. 4347)',
  'intelligence.searchPlayer': 'Spieler oder Allianz suchen',
  'intelligence.searchPlaceholder': 'Spieler oder Allianz (leer = ganze Region)',
  'intelligence.regionSearchTitle': 'Ganze Region {region}',
  'intelligence.errorUnknownRegion': 'Diese Region ist noch nicht im Netzwerk (prüfe die Nummer).',
  'intelligence.quotaRemainingValue': '{remaining} von {limit}',
  'intelligence.request': 'Suchen',
  'intelligence.requesting': 'Suche läuft…',
  'intelligence.quotaRemaining': 'Verbleibende Suchen heute',
  'intelligence.minChars': 'Gib mindestens 3 Zeichen ein, oder lass das Feld leer für die ganze Region.',
  'intelligence.regionRequired': 'Gib die zu durchsuchende Region an (z. B. 4347).',
  'intelligence.loading': 'Suchen werden geladen…',
  'intelligence.empty': 'Noch keine Suchen.',
  'intelligence.delete': 'Suche löschen',
  'intelligence.power': 'Macht',
  'intelligence.coordinates': 'Koordinaten',
  'intelligence.notFound': 'Keine Treffer.',
  'intelligence.noResult': 'Ergebnis ausstehend (noch keine Daten in dieser Region).',
  'intelligence.statusPending': 'In Warteschlange',
  'intelligence.statusProcessing': 'Suche',
  'intelligence.statusCompleted': 'Abgeschlossen',
  'intelligence.statusNotFound': 'Nicht gefunden',
  'intelligence.statusFailed': 'Ergebnis konnte nicht geladen werden',
  'intelligence.errorQuota': 'Du hast deine Suchen für heute aufgebraucht. Komm morgen wieder.',
  'intelligence.errorPending': 'Diese Suche läuft bereits.',
  'intelligence.errorNoRegion': 'Gib eine gültige Region zum Suchen an.',
  'intelligence.errorGeneric': 'Die Suche konnte nicht durchgeführt werden. Versuche es erneut.',
  'intelligence.search': 'Suche',
  'intelligence.resultsFound': '{count} Ergebnisse',
  'intelligence.showMore': '{count} weitere anzeigen',
  'intelligence.showLess': 'Weniger anzeigen',
  'intelligence.sortResults': 'Ergebnisse sortieren',
  'intelligence.sortPower': 'Macht',
  'intelligence.sortName': 'Name',
  'intelligence.lastSeen': 'Gesehen',
  'intelligence.deleteConfirm': '„{name}" aus dem Panel entfernen? Die Suche zählt weiterhin zu deinem Tageslimit.',
  // Token-Ökonomie + Kartierungsfortschritt (0.6.4, doc 17)
  'intelligence.tokensChip': '{n} Tokens',
  'intelligence.freeChip': '{n} gratis heute',
  'intelligence.freeTodayLabel': 'Gratis-Suche von heute',
  'intelligence.tokensBalance': 'Token-Guthaben',
  'intelligence.costsLine': 'Kosten in Tokens: Suche {search} · ganze Region {region}',
  'intelligence.noTokensCta': 'Keine Gratis-Suche und keine Tokens mehr: aktiviere die Aufklärungskartierung in den Einstellungen und deine Bots verdienen Tokens beim Scannen der Karte.',
  'intelligence.errorTokens': 'Nicht genug Tokens für diese Suche. Aktiviere die Kartierung, um mehr zu verdienen.',
  'intelligence.mappingTitle': 'Kartierungsfortschritt',
  'intelligence.mappingCells': '{scanned} von {total} Feldern',
  'intelligence.mappingToday': 'Heute von deinen Bots gescannt',
  'intelligence.mappingEarnedToday': 'Heute verdiente Tokens',
  'shieldWatch.toggle': 'Schild eines Spielers überwachen (dieser Bot kartiert nicht mehr)',
  'shieldWatch.title': 'Shield Monitor',
  'shieldWatch.historyTitle': 'Verlauf der Überwachung',
  'activity.title': 'Aufklärungs-Aktivität',
  'activity.showAll': 'Alle anzeigen ({count})',
  'activity.showLess': 'Weniger anzeigen',
  'activity.protected': 'Angriff erkannt: wir haben dich geschützt',
  'activity.attackHalted': 'Angriff erkannt: Schild NICHT bestätigt',
  'shieldWatch.captureOpen': 'Screenshot dieses Moments ansehen',
  'shieldWatch.captureLoading': 'Screenshot wird geladen…',
  'shieldWatch.captureAlt': 'Screenshot aus dem Moment der Entscheidung',
  'shieldWatch.captureGone': 'Dieser Screenshot ist weg: pro Bot werden nur die letzten 5 aufbewahrt.',
  'shieldWatch.captureClose': 'Schließen',
  'shieldWatch.help': 'Gib die Koordinaten der zu überwachenden Burg ein. Der Bot prüft regelmäßig, ob sie noch ein Schild hat, und mailt dir, sobald es fällt.',
  'shieldWatch.interval': 'Alle',
  'shieldWatch.region': 'Region',
  'shieldWatch.needCoords': 'Gib die X- und Y-Koordinaten des zu überwachenden Spielers ein.',
  'shieldWatch.idleNote': 'Die Prüfungen laufen in den Leerlaufzeiten des Bots; bei voller Farm können sie sich verzögern.',
  'shieldWatch.stateShielded': 'Ziel hat WEITERHIN ein Schild',
  'shieldWatch.stateConfirming': 'Kein Schild: wird bestätigt…',
  'shieldWatch.stateDown': 'Schild GEFALLEN!',
  'shieldWatch.stateMoved': 'Das Ziel ist nicht mehr an diesen Koordinaten',
  'shieldWatch.stateNoDetector': 'Überwachung nicht möglich: Detektor nicht verfügbar',
  'intelligence.mappingNoRegion': 'Wähle und prüfe eine Region unter Einstellungen → Aufklärungskartierung, um den Fortschritt zu sehen.',

  'support.title': 'Support',
  'support.subtitle': 'Kundendienst — wir antworten dir direkt hier.',
  'support.open': 'Support-Chat öffnen',
  'support.close': 'Support-Chat schließen',
  'support.empty': 'Brauchst du Hilfe? Beschreib das Problem (du kannst einen Screenshot anhängen) und wir antworten in diesem Chat.',
  'support.placeholder': 'Schreib deine Nachricht…',
  'support.send': 'Senden',
  'support.sending': 'Wird gesendet…',
  'support.attach': 'Screenshot anhängen',
  'support.attachRemove': 'Bild entfernen',
  'support.attachTooBig': 'Das Bild ist größer als 5 MB.',
  'support.attachNotImage': 'Es können nur Bilder angehängt werden.',
  'support.imageAlt': 'Angehängter Screenshot',
  'support.you': 'Du',
  'support.agent': 'Support',
  'support.sendError': 'Die Nachricht konnte nicht gesendet werden. Versuch es erneut.',
  'support.loading': 'Unterhaltung wird geladen…',
  'support.unreadLabel': '{count} neue Support-Nachrichten'
};

const pt: Record<TranslationKey, string> = {
  'app.account': 'Conta',
  'app.configuration': 'Configuracao',
  'app.configurationUnavailable': 'Configuracao nao disponivel',
  'app.faq': 'FAQ',
  'app.faqButton': 'Perguntas frequentes',
  'app.language': 'Idioma',
  'app.license': 'Licenca',
  'app.manageBilling': 'Gerenciar assinatura',
  'app.subscribe': 'Assinar',
  'app.openAccountMenu': 'Abrir menu da conta',
  'app.signOut': 'Sair',
  'app.billingError': 'Nao foi possivel abrir o portal de cobranca.',
  'app.billingNoStripe': 'Tua licença não é gerida pelo Stripe (acesso concedido manualmente): não há pagamentos para gerir.',
  'app.product': 'White Bot Desktop',
  'app.guide': 'Guia',
  'app.guideButton': 'Ver guia de instalacao',

  'auth.title': 'White Bot',
  'auth.subtitle': 'Plano Desktop · €11,99/mes · teste de 5 dias',
  'auth.betaSubtitle': 'Beta gratuita',
  'auth.intro': 'Entre para gerenciar seu bot.',
  'auth.email': 'Email',
  'auth.placeholderEmail': 'voce@email.com',
  'auth.sendLink': 'Enviar link de acesso',
  'auth.googleLogin': 'Entrar com Google',
  'auth.checkEmail': 'Verifique seu email: enviamos um link de acesso.',
  'auth.loadingSession': 'Carregando sessao…',

  'entitlement.checking': 'Verificando assinatura…',
  'entitlement.active': 'ativa',
  'entitlement.trialing': 'teste',
  'entitlement.past_due': 'pagamento pendente',
  'entitlement.canceled': 'cancelada',
  'entitlement.inactive': 'inativa',

  'pills.connected': 'cliente conectado',
  'pills.disconnected': 'cliente desconectado',

  'dashboard.actions': 'Acoes',
  'dashboard.autoBot': 'Auto Bot',
  'dashboard.collect': 'Coletar',
  'dashboard.trainTroops': 'Treinar tropas',
  'dashboard.autoHunt': 'Auto Hunt',
  'dashboard.stopBot': 'Parar bot',
  'dashboard.screenshot': 'Captura',
  'dashboard.mockView': 'Vista simulada — o cliente ainda nao enviou capturas',
  'dashboard.marches': 'Marchas',
  'dashboard.training': 'Treinamento',
  'dashboard.noRecentVerification': 'Sem verificacao recente',
  'dashboard.queue': 'Fila',
  'dashboard.state': 'Estado',
  'dashboard.activity': 'Atividade',
  'dashboard.lastReport': 'Ultimo relatorio',
  'dashboard.clientVersion': 'Versao do cliente',
  'dashboard.never': 'nunca',
  'dashboard.disconnectedNotice': 'O cliente nao reporta ha um tempo. Verifique se esta aberto no seu PC com o BlueStacks.',
  'dashboard.settingsSaved': 'Ajustes salvos.',
  'dashboard.client': 'Cliente',
  'dashboard.clientBody': 'Baixe e instale o cliente no seu PC (Windows + BlueStacks). Ele e ativado com a sua conta.',
  'dashboard.clientDownload': 'Baixar cliente (disponivel no GA)',
  'dashboard.clientDownloadReady': 'Baixar cliente (beta)',
  'dashboard.clientSmartscreen': 'Baixa como ZIP: extraia e execute o instalador. Beta sem assinatura: se o navegador ou o Windows avisarem, escolha "Manter" e "Mais informacoes → Executar assim mesmo".',

  'op.workingEyebrow': 'Operacao em andamento',
  'op.successEyebrow': 'Concluido',
  'op.errorEyebrow': 'Sem confirmacao',
  'op.warningEyebrow': 'Atencao',
  'op.startTitle': 'Iniciando {action}',
  'op.startBody': 'Ordem enviada. O cliente no seu PC vai captura-la em segundos.',
  'op.stopTitle': 'Parando o bot',
  'op.stopBody': 'Ordem enviada. O bot terminara o passo atual e vai parar.',
  'op.successStartTitle': 'Bot em execucao',
  'op.successStartBody': 'O cliente confirmou que esta executando.',
  'op.successStopTitle': 'Bot parado',
  'op.successStopBody': 'O cliente confirmou a parada.',
  'op.timeoutTitle': 'O cliente nao confirmou',
  'op.timeoutBody': 'A ordem fica salva e sera aplicada quando o cliente conectar. Verifique se o cliente esta aberto no seu PC.',
  'op.disconnectedTitle': 'Cliente desconectado',
  'op.disconnectedBody': 'Seu bot nao esta reportando agora. Abra o cliente no seu PC (com o BlueStacks) e tente de novo. Voce ainda pode enviar a ordem.',
  'op.sendAnyway': 'Enviar assim mesmo',
  'op.close': 'Fechar',
  'op.cancel': 'Cancelar',

  'settings.level': 'Nivel',
  'settings.marches': 'Marchas',
  'settings.amount': 'Quantidade por lote',
  'settings.type': 'Tipo',
  'settings.beasts': 'Feras (10 vigor)',
  'settings.terror': 'Terror polar (25 vigor)',
  'settings.timer': 'Espera do rally',
  'settings.up': 'Aumentar',
  'settings.down': 'Diminuir',
  'settings.resources.iron': 'Ferro',
  'settings.resources.coal': 'Carvao',
  'settings.resources.wood': 'Madeira',
  'settings.resources.meat': 'Carne',
  'settings.units.infantry': 'Infantaria',
  'settings.units.lancer': 'Lanceiros',
  'settings.units.marksman': 'Atiradores',
  'settings.autoHelp': 'Auto Ajuda',
  'settings.autoHeal': 'Auto Cura',
  'settings.autoShield': 'Auto Escudo',
  'settings.tasks': 'Tarefas',
  'settings.tasksEnable': 'Ativar tarefas automáticas',
  'settings.tasksHint': 'Recompensas e missões diárias: correio, heróis grátis, baú ocioso e missões de inteligência. Feitas automaticamente.',
  'settings.taskMail': 'Correio (coletar)',
  'settings.taskHeroes': 'Heróis grátis',
  'settings.taskIdle': 'Baú ocioso',
  'settings.taskIntel': 'Missões de inteligência',
  // Mapeamento de inteligência (0.6.4, doc 17)
  'settings.intelMapping': 'Mapeamento de inteligência',
  'settings.intelEnable': 'Ativar o mapeamento de inteligência',
  'settings.intelHint': 'Teus bots exploram o mapa da região escolhida e alimentam a rede de Intelligence. Cada célula escaneada te dá tokens para pagar buscas.',
  'settings.intelRegion': 'Região a mapear',
  'settings.intelRegionHint': 'De 3 a 5 dígitos (ex. 4347). Normalmente, a tua própria região.',
  'settings.intelCheckRegion': 'Verificar região',
  'settings.intelChecking': 'Verificando região…',
  'settings.intelRegionReady': 'A região {region} já está na rede ({total} células). Pronta para mapear.',
  'settings.intelRegionSeeded': 'Região {region} adicionada à rede ({total} células). Teus bots já podem mapeá-la.',
  'settings.intelRegionInvalid': 'Região inválida: usa de 3 a 5 dígitos.',
  'settings.intelRegionError': 'Não foi possível verificar a região. Tenta de novo.',
  'settings.intelCellsPerPass': 'Células por passada',
  'settings.intelCellsPerPassHint': 'Quantas células o bot escaneia em cada turno de mapeamento antes de seguir com as outras tarefas.',
  'settings.game': 'Jogo',
  'settings.gameLanguage': 'Idioma do jogo',
  'settings.gameLanguageEn': 'Ingles (padrao)',
  'settings.gameLanguageEs': 'Espanhol',
  'settings.gameLanguageHint': 'É o idioma do JOGO dentro do BlueStacks (não o deste site). O bot só sabe ler o jogo em inglês ou espanhol: define nas Configurações do jogo → Idioma e escolhe o mesmo aqui.',
  'settings.cryptids': 'Críptidos primeiro (eventos)',
  'settings.cryptidsHint': 'Com o evento Berserk Cryptid ativo e um Chifre de Críptido, a caça o gasta num críptido; sem evento ou chifres, caça normal (matar bestas é o que solta chifres).',
  'status.hunting_cryptid': 'Procurando um críptido do evento…',

  'subscribe.title': 'Teste o White Bot gratis por 5 dias',
  'subscribe.body': 'Execute o White Bot no seu proprio PC (Windows + BlueStacks) e controle-o por este site.',
  'subscribe.trial': 'TESTE',
  'subscribe.trialValue': '5 dias gratis',
  'subscribe.after': 'DEPOIS',
  'subscribe.afterValue': '€11,99 / mes',
  'subscribe.includes': 'INCLUI',
  'subscribe.includesValue': 'Coleta · Tropas · Caca · Cura · Escudo · Ajuda de alianca · Tarefas diarias',
  'subscribe.cancel': 'CANCELE',
  'subscribe.cancelValue': 'Quando quiser',
  'subscribe.cta': 'Comecar teste gratis (5 dias)',
  'subscribe.ctaOpening': 'Abrindo pagamento…',
  'subscribe.cardNote': 'Sem cartão para o teste. Só pagas se decidires assinar quando terminar.',
  'subscribe.trialCta': 'Começar o teste grátis de 5 dias — sem cartão',
  'subscribe.trialStarting': 'Ativando teu teste…',
  'subscribe.trialError': 'Não foi possível ativar o teste. Tenta de novo.',
  'subscribe.trialUsed': 'O teste grátis já foi usado nesta conta.',
  'subscribe.payCta': 'Assinar — 11,99 €/mês',
  'subscribe.endedTitle': 'Teu acesso terminou',
  'subscribe.endedBody': 'Teu teste ou assinatura terminou. Assina para continuar usando o White Bot no teu PC — tua configuração é mantida.',
  'subscribe.checkoutError': 'Erro ao iniciar o pagamento',
  'subscribe.hostedNote': 'Usa o servico hosted (roda em nossos servidores)? E outro produto:',
  'subscribe.hostedLink': 'ir para whitebot.app',

  // Beta gratuita até 15 de agosto de 2026 (domain/beta.ts)
  'beta.banner': 'Beta gratuita',
  'subscribe.betaTitle': 'White Bot, grátis durante a beta',
  'subscribe.betaTrial': 'BETA',
  'subscribe.betaTrialValue': 'Grátis até 15 ago 2026',
  'subscribe.betaTrialCta': 'Começar a beta grátis',
  'subscribe.betaCardNote': 'Sem cartão. O White Bot é grátis durante a beta, até 15 de agosto de 2026.',
  'subscribe.betaEndedNote': 'Estamos em beta gratuita. Se não tens acesso, fala connosco no chat de suporte.',

  'tutorial.title': 'Primeiros passos',
  'tutorial.intro': 'Tres passos para ter seu bot rodando no seu PC, controlado daqui.',
  'tutorial.step1Title': '1. Instale o cliente',
  'tutorial.step1Body': 'Baixe o cliente e instale-o no seu PC junto com o BlueStacks. O guia acompanha voce passo a passo.',
  'tutorial.step2Title': '2. Ative com seu email',
  'tutorial.step2Body': 'Abra o cliente e digite seu email. Quando conectar, voce vera "cliente conectado" aqui.',
  'tutorial.step3Title': '3. Controle o bot daqui',
  'tutorial.step3Body': 'Escolha modulos e ajustes, acione uma acao e acompanhe a captura e o estado neste painel.',
  'tutorial.dontShowAgain': 'Nao mostrar de novo',
  'tutorial.notNow': 'Agora nao',
  'tutorial.gotIt': 'Entendi',

  'pills.license': 'licença: {label}',

  'dashboard.refreshCapture': 'Atualizar captura',
  'dashboard.refreshCapturePending': 'Capturando…',
  'dashboard.refreshCaptureTimeout': 'O cliente não respondeu ao pedido de captura. Verifica se está aberto no teu PC.',

  'state.idle': 'inativo',
  'state.running': 'em execução',
  'state.waiting': 'em espera',
  'state.starting': 'iniciando',
  'state.verifying': 'verificando',
  'state.training': 'treinando',
  'state.collecting': 'coletando',
  'state.hunting': 'caçando',
  'state.helping': 'ajudando',
  'state.healing': 'curando',
  'state.recovering': 'recuperando',
  'state.reconnecting': 'reconectando',
  'state.shielded': 'escudo ativo',
  'state.error': 'erro',

  'status.no_client': 'Nenhum cliente conectado.',
  'status.idle': 'Em espera (parado pela web).',
  'status.running': 'Executando: {modules}',
  'status.running_none': 'Executando (sem módulos ativos).',
  'status.shielded': 'O Auto Escudo ativou a proteção: bot parado. Pressiona Parar e depois Iniciar para retomar.',
  'shieldAlarm.eyebrow': 'Auto Escudo',
  'shieldAlarm.title': 'Protegemos-te de um ataque',
  'shieldAlarm.body': 'Detetámos um ataque contra {name} e ativámos o teu escudo automaticamente. As tuas tropas foram recolhidas e a tua colónia está protegida. O bot parou por segurança: carrega em Iniciar quando quiseres retomar.',
  'shieldAlarm.time': 'Hora do ataque',
  'shieldAlarm.close': 'Entendido',
  'status.starting': 'Verificação de arranque: limpando a tela e verificando a colônia…',
  'status.waiting': 'Nada a fazer. Próxima verificação em {time}.',
  'status.backoff_wait': 'Recuperação em curso; nova tentativa em {time}.',
  'status.verifying_training': 'Verificando filas de treino…',
  'status.verifying_marches': 'Verificando filas de marcha…',
  'status.verifying_hunt': 'Verificando marchas de caça…',
  'status.training_collect': 'Coletando tropas concluídas: {units}',
  'langWarn.eyebrow': 'Idioma do jogo',
  'langWarn.title': 'Coloque o jogo em inglês',
  'langWarn.body': 'O White Bot não consegue ler seu jogo: o idioma não é suportado. No Whiteout Survival, abra Configurações → Language e escolha English. O bot continua sozinho assim que você trocar.',
  'langWarn.close': 'Entendi',
  'fleet.title': 'Meus bots',
  'fleet.counter': '{n}/{max} bots',
  'fleet.startAll': 'Iniciar todos',
  'fleet.stopAll': 'Parar todos',
  'fleet.delete': 'Excluir',
  'fleet.deleteConfirmTitle': 'Excluir {name}?',
  'fleet.deleteConfirmBody': 'Isso libera um espaço do limite da sua conta. Se o cliente desse bot ainda estiver aberto no seu PC, ele reaparecerá no próximo relatório.',
  'fleet.deleteConfirm': 'Excluir bot',
  'fleet.deleteCancel': 'Cancelar',
  'fleet.back': 'Todos os bots',
  'fleet.empty': 'Ainda não há bots. Abre o White Bot Client no teu PC (um console por instância do BlueStacks) e eles aparecerão aqui sozinhos.',
  'fleet.defaultName': 'Bot',
  'fleet.lastReport': 'Último relatório',
  'status.unsupported_language': 'Idioma do jogo não suportado — mude para inglês',
  'status.language_autofix': 'Idioma não suportado: mudando o jogo para inglês automaticamente…',
  'status.dailies': 'Coletando recompensas diárias…',
  'status.intel_missions': 'Missões de inteligência…',
  'status.intel_map_scanning': 'Inteligência: {cells} células escaneadas (+{tokens} tokens)',
  'status.intel_map_region_done': 'Inteligência: região em dia',
  'status.intel_map_detector_missing': 'Inteligência: detector indisponível',
  'status.intel_map_region_missing': 'Inteligência: configura a região em Ajustes',
  'state.intel': 'Inteligência',
  'state.dailies': 'Recompensas diárias',
  'status.language_fixed': 'Idioma mudado para inglês; aguardando o reinício do jogo…',
  'state.fixing_language': 'Mudando idioma',
  'state.blocked_language': 'Idioma não suportado',
  'status.training_units': 'Treinando unidades: {units} ({percent}%)',
  'status.collecting_send': 'Enviando marcha de coleta: {resource} (nível {level})',
  'status.hunting_send': 'Enviando ataque de caça: {type} nível {level}',
  'status.helping': 'Verificando Ajuda automática da aliança…',
  'status.healing': 'Verificando Cura automática…',
  'status.recovering_attempt': 'Recuperando ({attempt}/{max}); nova tentativa em {time}…',
  'status.recovered_ui': 'Tela restaurada após uma falha ({task}). Continuando…',
  'status.relaunching': 'Reiniciando BlueStacks/jogo após falhas repetidas…',
  'status.relaunched': 'Jogo reiniciado após uma falha. Retomando em {time}.',
  'status.hand_guide': 'Mão-guia do tutorial detectada: tocando onde ela aponta…',
  'status.reconnecting': 'Sessão aberta em outro dispositivo: pressionando Reconectar…',
  'status.backoff_init_error': 'Erro de inicialização ({detail}). Nova tentativa automática em {time}.',
  'status.backoff_no_adb': 'Sem conexão com o BlueStacks (ADB). Verifica se o BlueStacks está aberto. Nova tentativa em {time}.',
  'status.backoff_failure': 'Falha repetida ({task}). Nova tentativa automática em {time}.',
  'status.backoff_blocked': 'Tela bloqueada ({kind}). Nova tentativa automática em {time}.',

  'statusTask.verify_training': 'verificação das filas de treino',
  'statusTask.collect_completed': 'coleta de tropas concluídas',
  'statusTask.train_flow': 'fluxo de treino',
  'statusTask.return_after_training': 'volta à colônia após o treino',
  'statusTask.verify_marches': 'verificação de marchas',
  'statusTask.collect_flow': 'fluxo de coleta',
  'statusTask.hunt_capture': 'captura do estado de caça',
  'statusTask.verify_hunt': 'verificação das marchas de caça',
  'statusTask.hunt_vigor': 'leitura do vigor de caça',
  'statusTask.hunt_flow': 'fluxo de caça',
  'statusTask.auto_heal': 'cura automática',
  'statusTask.startup_return': 'volta à colônia no arranque',
  'statusTask.startup_screen': 'tela não reconhecida no arranque',
  'statusTask.unexpected': 'erro inesperado',

  'intelligence.title': 'Intelligence',
  'intelligence.intro': 'Consulta jogadores e alianças reunidos pela rede do White Bot. {limit} buscas por dia.',
  'intelligence.region': 'Região',
  'intelligence.regionPlaceholder': 'Região (ex. 4347)',
  'intelligence.searchPlayer': 'Buscar jogador ou aliança',
  'intelligence.searchPlaceholder': 'Jogador ou aliança (vazio = região inteira)',
  'intelligence.regionSearchTitle': 'Região inteira {region}',
  'intelligence.errorUnknownRegion': 'Essa região ainda não está na rede (confere o número).',
  'intelligence.quotaRemainingValue': '{remaining} de {limit}',
  'intelligence.request': 'Buscar',
  'intelligence.requesting': 'Buscando…',
  'intelligence.quotaRemaining': 'Buscas disponíveis hoje',
  'intelligence.minChars': 'Digita pelo menos 3 caracteres, ou deixa vazio para listar a região inteira.',
  'intelligence.regionRequired': 'Indica a região onde buscar (ex. 4347).',
  'intelligence.loading': 'Carregando buscas…',
  'intelligence.empty': 'Nenhuma busca ainda.',
  'intelligence.delete': 'Excluir busca',
  'intelligence.power': 'Poder',
  'intelligence.coordinates': 'Coordenadas',
  'intelligence.notFound': 'Sem correspondências.',
  'intelligence.noResult': 'Resultado pendente (ainda sem dados nessa região).',
  'intelligence.statusPending': 'Na fila',
  'intelligence.statusProcessing': 'Buscando',
  'intelligence.statusCompleted': 'Concluída',
  'intelligence.statusNotFound': 'Não encontrado',
  'intelligence.statusFailed': 'Não foi possível obter o resultado',
  'intelligence.errorQuota': 'Esgotaste tuas buscas de hoje. Volta amanhã.',
  'intelligence.errorPending': 'Essa busca já está em andamento.',
  'intelligence.errorNoRegion': 'Indica uma região válida onde buscar.',
  'intelligence.errorGeneric': 'A busca não pôde ser realizada. Tenta de novo.',
  'intelligence.search': 'Busca',
  'intelligence.resultsFound': '{count} resultados',
  'intelligence.showMore': 'Ver mais {count}',
  'intelligence.showLess': 'Ver menos',
  'intelligence.sortResults': 'Ordenar resultados',
  'intelligence.sortPower': 'Poder',
  'intelligence.sortName': 'Nome',
  'intelligence.lastSeen': 'Visto',
  'intelligence.deleteConfirm': 'Excluir "{name}" do painel? A busca continua contando para teu limite diário.',
  // Economia de tokens + progresso do mapeamento (0.6.4, doc 17)
  'intelligence.tokensChip': '{n} tokens',
  'intelligence.freeChip': '{n} grátis hoje',
  'intelligence.freeTodayLabel': 'Busca grátis de hoje',
  'intelligence.tokensBalance': 'Saldo de tokens',
  'intelligence.costsLine': 'Custo em tokens: busca {search} · região inteira {region}',
  'intelligence.noTokensCta': 'Sem busca grátis nem tokens: ativa o mapeamento de inteligência nos Ajustes e teus bots vão ganhar tokens escaneando o mapa.',
  'intelligence.errorTokens': 'Tokens insuficientes para esta busca. Ativa o mapeamento para ganhar mais.',
  'intelligence.mappingTitle': 'Progresso do mapeamento',
  'intelligence.mappingCells': '{scanned} de {total} células',
  'intelligence.mappingToday': 'Escaneadas hoje pelos teus bots',
  'intelligence.mappingEarnedToday': 'Tokens ganhos hoje',
  'shieldWatch.toggle': 'Vigiar o escudo de um jogador (este bot deixa de mapear)',
  'shieldWatch.title': 'Shield Monitor',
  'shieldWatch.historyTitle': 'Histórico da vigilância',
  'activity.title': 'Atividade de inteligência',
  'activity.showAll': 'Ver tudo ({count})',
  'activity.showLess': 'Mostrar menos',
  'activity.protected': 'Ataque detetado: protegemos-te com escudo',
  'activity.attackHalted': 'Ataque detetado: escudo NÃO confirmado',
  'shieldWatch.captureOpen': 'Ver a captura desse momento',
  'shieldWatch.captureLoading': 'A carregar a captura…',
  'shieldWatch.captureAlt': 'Captura do momento em que o bot decidiu',
  'shieldWatch.captureGone': 'Esta captura já não está: só se guardam as 5 últimas de cada bot.',
  'shieldWatch.captureClose': 'Fechar',
  'shieldWatch.help': 'Escreve as coordenadas do castelo a vigiar. O bot verificará periodicamente se ainda tem escudo e avisa-te por email quando cair.',
  'shieldWatch.interval': 'A cada',
  'shieldWatch.region': 'Região',
  'shieldWatch.needCoords': 'Indica as coordenadas X e Y do jogador a vigiar.',
  'shieldWatch.idleNote': 'As verificações ocorrem nos tempos livres do bot; com a fazenda ocupada podem espaçar-se.',
  'shieldWatch.stateShielded': 'O alvo AINDA tem escudo',
  'shieldWatch.stateConfirming': 'Sem escudo: a confirmar…',
  'shieldWatch.stateDown': 'Escudo CAIU!',
  'shieldWatch.stateMoved': 'O alvo já não está nessas coordenadas',
  'shieldWatch.stateNoDetector': 'Não é possível vigiar: detetor indisponível',
  'intelligence.mappingNoRegion': 'Escolhe e verifica uma região em Ajustes → Mapeamento de inteligência para ver o progresso.',

  'support.title': 'Suporte',
  'support.subtitle': 'Atendimento ao cliente — respondemos aqui mesmo.',
  'support.open': 'Abrir o chat de suporte',
  'support.close': 'Fechar o chat de suporte',
  'support.empty': 'Precisa de ajuda? Conta o problema (podes anexar uma captura) e responderemos neste chat.',
  'support.placeholder': 'Escreve tua mensagem…',
  'support.send': 'Enviar',
  'support.sending': 'Enviando…',
  'support.attach': 'Anexar captura',
  'support.attachRemove': 'Remover a imagem',
  'support.attachTooBig': 'A imagem passa de 5 MB.',
  'support.attachNotImage': 'Só é possível anexar imagens.',
  'support.imageAlt': 'Captura anexada',
  'support.you': 'Tu',
  'support.agent': 'Suporte',
  'support.sendError': 'Não foi possível enviar a mensagem. Tenta de novo.',
  'support.loading': 'Carregando a conversa…',
  'support.unreadLabel': '{count} novas mensagens do suporte'
};

export type TranslationKey = keyof typeof es;

const translations: Record<AppLanguage, Record<TranslationKey, string>> = { es, en, fr, de, pt, ru, ko, zh, ja, tr };

export function detectLanguage(): AppLanguage {
  // 1) ?lang= explícito en la URL (enlaces compartibles + export de PDFs de la guía)
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang && urlLang in translations) return urlLang as AppLanguage;
  // 2) elección previa del usuario (selector del menú WB)
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in translations) return stored as AppLanguage;
  // 3) idioma del navegador; si no está soportado, inglés
  const browser = (navigator.language || 'en').toLowerCase();
  for (const { code } of languages) {
    if (browser.startsWith(code)) return code;
  }
  return 'en';
}

interface I18nValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(detectLanguage);

  // El <html lang> estático es "en" (meta para crawlers, que no ejecutan JS);
  // en runtime se ajusta al idioma real del usuario (accesibilidad/SEO).
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nValue>(() => {
    const setLanguage = (next: AppLanguage) => {
      localStorage.setItem(STORAGE_KEY, next);
      setLanguageState(next);
    };
    const t = (key: TranslationKey, params?: Record<string, string>) => {
      let text = translations[language][key] ?? translations.en[key] ?? key;
      for (const [name, replacement] of Object.entries(params ?? {})) {
        text = text.replace(`{${name}}`, replacement);
      }
      return text;
    };
    return { language, setLanguage, t };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within I18nProvider');
  return context;
}

// Para tests de paridad de claves entre idiomas:
export const translationDictionaries = translations;
